import { normalizePageResponse } from "../lib/pagination";
import { imsGet, imsPost, imsPostBlob, imsPut } from "../api/api";
import {
  downloadBlob,
  parseContentDispositionFilename,
} from "../utils/downloadFile";
import { searchProducts, toUiProductStatus, fetchMetricsByStatus } from "./productCatalogService";
import {
  searchWarehousesByStatus,
  fetchWarehouseById,
} from "./warehouseService";
import {
  searchVendors,
  searchVendorsByStatus,
  fetchVendorById,
} from "./productVendorService";
import {
  searchCustomers,
  searchCustomerLocations,
  searchCustomerContacts,
} from "./fsmService";
import { customerService } from "./customerService";
import {
  normalizeStockPositionOtherFilter,
  STOCK_POSITION_OTHER_FILTER as POSITION_OTHER_FILTER,
} from "../modules/inventory-management/utils/aggregateStockPositions";
const INVENTORY_RECORD_ADD = "IMS05A";
const INVENTORY_RECORD_SEARCH = "IMS05S";
const INVENTORY_RECORD_UPDATE = "IMS05U";
const DELIVERY_CHALLAN_SEARCH = "IMS11S";
const DELIVERY_CHALLAN_UPDATE = "IMS11U";
const DELIVERY_CHALLAN_DOWNLOAD = "IMS11DD";
const STOCK_TRANSFER_ADD = "IMS05A";
const STOCK_POSITION_SEARCH = "IMS05S";
const STOCK_JOURNAL_SEARCH = "IMS05S";
const REORDER_ALERTS_SEARCH = "IMS05R";
const CUSTOMER_ALLOCATION_ADD = "IMS08A";
const CUSTOMER_ALLOCATION_SEARCH = "IMS08S";
const CUSTOMER_ALLOCATION_UPDATE = "IMS08U";
const REVOKE_UNLOCK_SEARCH = "IMS12S";
const REVOKE_UNLOCK_UPDATE = "IMS12U";
const TRANSFER_PAGE_SIZE = 10;
const ALLOCATION_LOOKUP_SIZE = 10;

const DEAD_STOCK_DAYS = 180;

let metricNameByIdCache = null;
let metricNameByIdInflight = null;

async function getMetricNameByIdMap() {
  if (metricNameByIdCache) return metricNameByIdCache;
  if (metricNameByIdInflight) return metricNameByIdInflight;

  metricNameByIdInflight = (async () => {
    try {
      const metrics = await fetchMetricsByStatus(true, { page: 0, size: 200 });
      const map = new Map();
      for (const m of metrics || []) {
        const id =
          m.metricId != null && m.metricId !== ""
            ? String(m.metricId)
            : m.id != null && m.id !== ""
              ? String(m.id)
              : "";
        if (!id) continue;
        const name = String(m.name || m.abbreviation || m.label || "").trim();
        if (name) map.set(id, name);
      }
      metricNameByIdCache = map;
      return map;
    } catch (err) {
      console.warn("Failed to load metric name lookup:", err);
      return new Map();
    } finally {
      metricNameByIdInflight = null;
    }
  })();

  return metricNameByIdInflight;
}

function resolveMetricDisplayName(metricId, fallbackName, metricMap) {
  const id = metricId != null && metricId !== "" ? String(metricId) : "";
  const fallback = String(fallbackName || "").trim();
  if (id && metricMap?.has(id)) return metricMap.get(id);
  if (fallback && fallback !== id && !/^\d+$/.test(fallback)) return fallback;
  if (fallback && fallback !== id) return fallback;
  return fallback || "—";
}

function unwrapData(response) {
  if (response == null) return response;
  if (response.data !== undefined) {
    if (
      response.data &&
      typeof response.data === "object" &&
      !Array.isArray(response.data) &&
      response.data.data !== undefined
    ) {
      return response.data.data;
    }
    return response.data;
  }
  return response;
}

function unwrapPage(response, options = { paginate: false }) {
  return normalizePageResponse(response, options);
}

function unwrapItem(response) {
  const data = unwrapData(response);
  if (Array.isArray(data)) return data[0] || null;
  return data && typeof data === "object" ? data : null;
}

function firstDefined(...values) {
  for (const v of values) {
    if (v !== undefined && v !== null && v !== "") return v;
  }
  return undefined;
}

function toNumberOrNull(value) {
  if (value === "" || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isNaN(n) ? null : n;
}

function toFiniteNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function isPastDeadStockWindow(lastOutwardMovementDate) {
  if (!lastOutwardMovementDate) return false;
  const moved = new Date(lastOutwardMovementDate);
  if (!Number.isFinite(moved.getTime())) return false;
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - DEAD_STOCK_DAYS);
  return moved < cutoff;
}

function resolveIsDeadStock(api, lastOutwardMovementDate) {
  const flag = firstDefined(api?.isDeadStock, api?.deadStock, api?.is_dead_stock, api?.dead_stock);
  if (typeof flag === "boolean") return flag;
  if (flag === 1 || flag === "1" || flag === "true" || flag === "TRUE") return true;
  if (flag === 0 || flag === "0" || flag === "false" || flag === "FALSE") return false;
  const label = String(flag || "").trim().toLowerCase();
  if (label === "dead stock" || label === "dead") return true;
  if (label === "active stock" || label === "active") return false;
  // Some APIs send lastOutwardMovement as a dead-stock boolean
  const lastOutward = firstDefined(api?.lastOutwardMovement, api?.last_outward_movement);
  if (typeof lastOutward === "boolean") return lastOutward;
  if (lastOutward === 1 || lastOutward === "1" || lastOutward === "true" || lastOutward === "TRUE") {
    return true;
  }
  if (lastOutward === 0 || lastOutward === "0" || lastOutward === "false" || lastOutward === "FALSE") {
    return false;
  }
  return isPastDeadStockWindow(lastOutwardMovementDate);
}

function toUiBatchStatus(status) {
  const raw = String(status || "").trim();
  if (!raw) return "Available";
  const key = raw.toUpperCase().replace(/[\s-]+/g, "_");
  if (key === "LOW_STOCK") return "Low Stock";
  if (key === "EXPIRED_WARRANTY") return "Expired Warranty";
  if (key === "AVAILABLE") return "Available";
  if (key === "RESERVED") return "Reserved";
  return raw;
}

function emptyToNull(value) {
  if (value === undefined || value === null) return null;
  const s = String(value).trim();
  if (s === "" || s === "—") return null;
  return s;
}

/** Join non-empty address parts for challan / transfer party dropdowns. */
function formatPartyAddress({
  locationName,
  addressLine1,
  addressLine2,
  city,
  state,
  pincode,
  address,
} = {}) {
  const parts = [
    locationName,
    addressLine1,
    addressLine2,
    city,
    state,
    pincode,
    address,
  ]
    .map((part) => String(part || "").trim())
    .filter(Boolean);
  return Array.from(new Set(parts)).join(", ");
}

function normalizeImagesPayload(images) {
  if (images == null || images === "") return null;
  if (!Array.isArray(images)) return [images];
  return images.length > 0 ? images : null;
}

function normalizeApiImageList(raw) {
  if (raw == null || raw === "") return [];
  let list = raw;
  if (!Array.isArray(list) && typeof list === "object") {
    list =
      list.images ??
      list.files ??
      list.content ??
      list.inventoryImages ??
      list.defectiveImages ??
      list.data ??
      [];
  }
  if (!Array.isArray(list)) {
    list = list == null || list === "" ? [] : [list];
  }
  return list
    .map((item, index) => {
      if (typeof item === "string" && item.trim()) {
        const isDataUrl = item.startsWith("data:");
        return {
          key: `api-${index}`,
          name: `Image ${index + 1}`,
          contentType: isDataUrl
            ? item.slice(5, item.indexOf(";")) || "image/jpeg"
            : "image/jpeg",
          fileByte: isDataUrl ? item.split(",")[1] || item : item,
          existing: true,
        };
      }
      if (!item || typeof item !== "object") return null;
      const fileByte =
        item.fileByte ?? item.file_byte ?? item.base64 ?? item.content ?? null;
      const url = item.url || item.imageUrl || item.image_url || null;
      if (!fileByte && !url) return null;
      return {
        key: `api-${index}-${item.fileName || item.file_name || item.name || index}`,
        name:
          item.fileName ||
          item.file_name ||
          item.name ||
          `Image ${index + 1}`,
        contentType:
          item.contentType ||
          item.content_type ||
          item.mimeType ||
          "image/jpeg",
        fileByte,
        url,
        existing: true,
      };
    })
    .filter(Boolean);
}

function displayOrId(name, id) {
  if (name != null && String(name).trim() !== "") return String(name).trim();
  if (id != null && id !== "") return String(id);
  return "—";
}

function isUnresolvedName(name, id) {
  if (id == null || id === "") return false;
  const sid = String(id);
  const sname = name == null ? "" : String(name).trim();
  return !sname || sname === "—" || sname === sid;
}

async function resolveCustomerNameById(customerId) {
  const id = String(customerId);
  try {
    const customer = await customerService.getCustomerById(id);
    const name =
      customer?.companyName ||
      customer?.customerName ||
      customer?.name ||
      null;
    if (name && String(name).trim() !== "") return String(name).trim();
  } catch (_) {
    /* fall through */
  }
  try {
    const byTerm = await searchCustomers({
      page: 0,
      size: 10,
      sortBy: "customerId",
      sortDir: "ASC",
      searchTerm: id,
    });
    const hit = (byTerm.items || []).find((c) => String(c.id) === id);
    if (hit?.name) return hit.name;
  } catch (_) {
    /* fall through */
  }
  return null;
}

/**
 * Inventory-record search now returns productName / warehouseName / vendorName.
 * Customer name is not returned — resolve those on the UI via CRM.
 */
async function enrichInventoryRecordCustomerNames(rows = []) {
  const customerIds = new Set();
  for (const row of rows) {
    if (
      !usesPurchaseFields(row.sourceType) &&
      isUnresolvedName(row.partyName, row.customerId)
    ) {
      customerIds.add(String(row.customerId));
    }
  }
  if (customerIds.size === 0) return rows;

  const customerMap = new Map();
  await Promise.all(
    [...customerIds].map(async (id) => {
      const name = await resolveCustomerNameById(id);
      if (name) customerMap.set(id, String(name));
    })
  );

  return rows.map((row) => {
    if (
      usesPurchaseFields(row.sourceType) ||
      !customerMap.has(String(row.customerId))
    ) {
      return row;
    }
    const customerName = customerMap.get(String(row.customerId));
    return {
      ...row,
      customerName,
      partyName: customerName,
    };
  });
}

const API_SOURCE_TYPES = new Set([
  "Purchase_From_Vendor",
  "Warranty_Replacement_From_Vendor",
  "Defective_Replacement_From_Vendor",
  "Excess_Return_From_Customer",
  "Warranty_Replacement_From_Customer",
  "Defective_Replacement_From_Customer",
]);

function toApiSourceType(uiSourceType) {
  const value = String(uiSourceType || "").trim();
  if (API_SOURCE_TYPES.has(value)) return value;

  if (
    value === "Warranty Replacement from Customer" ||
    value === "Warranty Replacement From Customer"
  ) {
    return "Warranty_Replacement_From_Customer";
  }
  if (
    value === "Defective Replacement from Customer" ||
    value === "Defective Replacement From Customer"
  ) {
    return "Defective_Replacement_From_Customer";
  }
  if (
    value === "Return from Customer" ||
    value === "Excess Material Return" ||
    value === "Excess Return" ||
    value === "Excess Return from Customer" ||
    value === "Excess Return From Customer" ||
    value === "Excess_Return" ||
    value === "Return_from_Customer" ||
    value === "Return_From_Customer"
  ) {
    return "Excess_Return_From_Customer";
  }
  if (
    
    value === "Warranty Replacement from Vendor" ||
    value === "Warranty_Replacement_From_Vendor"
    
  ) {
    return "Warranty_Replacement_From_Vendor";
  }
  if (
    value === "Defective Replacement from Vendor" ||
    value === "Defective Replacement From Vendor"
  ) {
    return "Defective_Replacement_From_Vendor";
  }
  return "Purchase_From_Vendor";
}

function toUpdateApiSourceType(uiSourceType) {
  return toApiSourceType(uiSourceType);
}

function toUiSourceType(apiSourceType) {
  return toApiSourceType(apiSourceType);
}

/** True when the inventory-in form should show vendor/purchase fields (vs customer). */
export function usesPurchaseFields(sourceType) {
  const api = toApiSourceType(sourceType);
  return (
    api === "Purchase_From_Vendor" ||
    api === "Warranty_Replacement_From_Vendor" ||
    api === "Defective_Replacement_From_Vendor"
  );
}

/** True when warranty/defective replacement-from-vendor needs a stock-journal reference. */
export function usesWarDefRefJournal(sourceType) {
  const api = toApiSourceType(sourceType);
  return (
    api === "Warranty_Replacement_From_Vendor" ||
    api === "Defective_Replacement_From_Vendor"
  );
}

/** Customer warranty/defective replacement sends customerWarrantyReplacementType. */
export function usesCustomerWarrantyReplacementType(sourceType) {
  const api = toApiSourceType(sourceType);
  return (
    api === "Warranty_Replacement_From_Customer" ||
    api === "Defective_Replacement_From_Customer"
  );
}

/** Stock-journal movement type used to populate the warranty/defective reference dropdown. */
export function warDefRefMovementType(sourceType) {
  const api = toApiSourceType(sourceType);
  if (api === "Warranty_Replacement_From_Vendor") {
    return "OEM Warranty Replacement Request to Vendor";
  }
  if (api === "Defective_Replacement_From_Vendor") {
    return "Defective Replacement Request to Vendor";
  }
  return null;
}

function usesVendorApiSourceType(apiSourceType) {
  return (
    apiSourceType === "Purchase_From_Vendor" ||
    apiSourceType === "Purchase_from_Vendor" ||
    apiSourceType === "Warranty_Replacement_From_Vendor" ||
    apiSourceType === "OEM_Warranty_Replacement_From_Vendor" ||
    apiSourceType === "Defective_Replacement_From_Vendor" ||
    apiSourceType === "Purchase" ||
    apiSourceType === "OEM_Warranty_Replacement"
  );
}

function mapInventoryRecordListRow(api) {
  if (!api || typeof api !== "object") return null;
  const inventoryInId = firstDefined(
    api.inventoryInId,
    api.inventory_in_id,
    api.id
  );
  if (inventoryInId == null || inventoryInId === "") return null;

  const productId = firstDefined(api.productId, api.product_id);
  const vendorId = firstDefined(api.vendorId, api.vendor_id);
  const customerId = firstDefined(api.customerId, api.customer_id);
  const warehouseLocationId = firstDefined(
    api.warehouseLocationId,
    api.warehouse_location_id,
    api.warehouseId
  );
  const sourceType = toUiSourceType(
    firstDefined(api.sourceType, api.source_type)
  );

  return {
    id: String(inventoryInId),
    inventoryInId: String(inventoryInId),
    sourceType,
    batchNumber: emptyToNull(firstDefined(api.batchNumber, api.batch_number)) || "—",
    receiptDate: firstDefined(api.receiptDate, api.receipt_date, api.returnDate) || null,
    productId: productId != null ? String(productId) : "",
    productName: displayOrId(
      firstDefined(api.productName, api.product_name),
      productId
    ),
    receivedQuantity: Number(
      firstDefined(api.receivedQuantity, api.received_quantity)
    ) || 0,
    defectiveQuantity: Number(
      firstDefined(api.defectiveQuantity, api.defective_quantity)
    ) || 0,
    acceptedQuantity: Number(
      firstDefined(api.acceptedQuantity, api.accepted_quantity)
    ) || 0,
    purchasePricePerUnit: Number(
      firstDefined(api.purchasePricePerUnit, api.purchase_price_per_unit)
    ) || 0,
    vendorId: vendorId != null ? String(vendorId) : "",
    vendorName: displayOrId(
      firstDefined(api.vendorName, api.vendor_name),
      vendorId
    ),
    customerId: customerId != null ? String(customerId) : "",
    customerName: displayOrId(
      firstDefined(api.customerName, api.customer_name),
      customerId
    ),
    warehouseLocationId:
      warehouseLocationId != null ? String(warehouseLocationId) : "",
    warehouseName: displayOrId(
      firstDefined(api.warehouseName, api.warehouse_name, api.warehouseLocationName),
      warehouseLocationId
    ),
    poNumber: emptyToNull(firstDefined(api.poNumber, api.po_number)) || "—",
    partyName: usesPurchaseFields(sourceType)
      ? displayOrId(
          firstDefined(api.vendorName, api.vendor_name),
          vendorId
        )
      : displayOrId(
          firstDefined(api.customerName, api.customer_name),
          customerId
        ),
  };
}

function mapInventoryRecordDetail(api) {
  if (!api || typeof api !== "object") return null;
  const inventoryInId = firstDefined(
    api.inventoryInId,
    api.inventory_in_id,
    api.id
  );
  if (inventoryInId == null || inventoryInId === "") return null;

  const sourceType = toUiSourceType(
    firstDefined(api.sourceType, api.source_type)
  );
  const productId = firstDefined(api.productId, api.product_id);
  const vendorId = firstDefined(api.vendorId, api.vendor_id);
  const customerId = firstDefined(api.customerId, api.customer_id);
  const warehouseLocationId = firstDefined(
    api.warehouseLocationId,
    api.warehouse_location_id,
    api.warehouseId
  );

  return {
    inventoryInId: String(inventoryInId),
    sourceType,
    productId: productId != null ? String(productId) : "",
    productName: displayOrId(
      firstDefined(api.productName, api.product_name),
      productId
    ),
    batchNumber: emptyToNull(firstDefined(api.batchNumber, api.batch_number)) || "",
    receiptDate: emptyToNull(firstDefined(api.receiptDate, api.receipt_date)) || "",
    returnDate: emptyToNull(firstDefined(api.returnDate, api.return_date)) || "",
    receivedQty: Number(
      firstDefined(api.receivedQuantity, api.received_quantity)
    ) || 0,
    defectiveQty: Number(
      firstDefined(api.defectiveQuantity, api.defective_quantity)
    ) || 0,
    remarks: emptyToNull(firstDefined(api.remarks, api.remark)) || "",
    inventoryImages: firstDefined(api.inventoryImages, api.inventory_images) || [],
    defectiveImages: firstDefined(api.defectiveImages, api.defective_images) || [],
    acceptedQuantity: Number(
      firstDefined(api.acceptedQuantity, api.accepted_quantity)
    ) || 0,
    purchasePricePerUnit:
      firstDefined(api.purchasePricePerUnit, api.purchase_price_per_unit) ?? "",
    warrantyStartDate:
      emptyToNull(firstDefined(api.warrantyStartDate, api.warranty_start_date)) ||
      "",
    warrantyEndDate:
      emptyToNull(firstDefined(api.warrantyEndDate, api.warranty_end_date)) || "",
    vendorId: vendorId != null ? String(vendorId) : "",
    vendorName: displayOrId(
      firstDefined(api.vendorName, api.vendor_name),
      vendorId
    ),
    customerId: customerId != null ? String(customerId) : "",
    customerName: displayOrId(
      firstDefined(api.customerName, api.customer_name),
      customerId
    ),
    poNumber: emptyToNull(firstDefined(api.poNumber, api.po_number)) || "",
    vendorInvoiceNumber:
      emptyToNull(
        firstDefined(api.vendorInvoiceNumber, api.vendor_invoice_number)
      ) || "",
    customerInvoiceId:
      emptyToNull(
        firstDefined(api.customerInvoiceId, api.customer_invoice_id)
      ) || "",
    invoiceDate: emptyToNull(firstDefined(api.invoiceDate, api.invoice_date)) || "",
    projectName: emptyToNull(firstDefined(api.projectName, api.project_name)) || "",
    iofNumber: emptyToNull(firstDefined(api.iofNumber, api.iof_number)) || "",
    warehouseId:
      warehouseLocationId != null ? String(warehouseLocationId) : "",
    warehouseName: displayOrId(
      firstDefined(api.warehouseName, api.warehouse_name, api.warehouseLocationName),
      warehouseLocationId
    ),
    warDefRefJournalId: (() => {
      const ref = firstDefined(
        api.warDefRefJournalId,
        api.war_def_ref_journal_id
      );
      return ref != null && String(ref).trim() !== "" ? String(ref) : "";
    })(),
    customerWarrantyReplacementType:
      emptyToNull(
        firstDefined(
          api.customerWarrantyReplacementType,
          api.customer_warranty_replacement_type
        )
      ) || "",
  };
}

function mapInventoryReceiptUpdatePayload(payload = {}) {
  const sourceType = toUpdateApiSourceType(payload.sourceType);
  const receivedQuantity =
    toNumberOrNull(payload.receivedQuantity ?? payload.receivedQty) ?? 0;
  const defectiveQuantity =
    toNumberOrNull(payload.defectiveQuantity ?? payload.defectiveQty) ?? 0;
  const acceptedQuantity =
    toNumberOrNull(payload.acceptedQuantity) ??
    Math.max(0, receivedQuantity - defectiveQuantity);

  const body = {
    sourceType,
    productId: toNumberOrNull(payload.productId),
    warehouseLocationId: toNumberOrNull(
      payload.warehouseLocationId ?? payload.warehouseId
    ),
    receivedQuantity,
    defectiveQuantity,
    acceptedQuantity,
    purchasePricePerUnit: toNumberOrNull(
      payload.purchasePricePerUnit ?? payload.purchasePrice
    ),
    batchNumber: emptyToNull(payload.batchNumber),
    warrantyStartDate: emptyToNull(payload.warrantyStartDate),
    warrantyEndDate: emptyToNull(payload.warrantyEndDate),
    remarks: emptyToNull(payload.remarks),
    inventoryImages: normalizeImagesPayload(
      payload.inventoryImages ?? payload.inventory_images
    ),
    defectiveImages: normalizeImagesPayload(
      payload.defectiveImages ?? payload.defective_images
    ),
  };

  if (usesVendorApiSourceType(sourceType)) {
    body.receiptDate = emptyToNull(payload.receiptDate);
    body.vendorId = toNumberOrNull(payload.vendorId);
    body.poNumber = emptyToNull(payload.poNumber);
    body.vendorInvoiceNumber = emptyToNull(
      payload.vendorInvoiceNumber ?? payload.invoiceNumber
    );
    body.invoiceDate = emptyToNull(payload.invoiceDate);
    body.iofNumber = emptyToNull(payload.iofNumber);
    body.customerId = null;
    body.customerInvoiceId = null;
    body.projectName = null;
  } else {
    const returnDate = emptyToNull(payload.returnDate);
    body.receiptDate = emptyToNull(payload.receiptDate) || returnDate;
    body.customerId = toNumberOrNull(payload.customerId);
    body.customerInvoiceId = emptyToNull(payload.customerInvoiceId);
    body.projectName = emptyToNull(payload.projectName);
    body.vendorId = null;
    body.poNumber = null;
    body.vendorInvoiceNumber = null;
    body.invoiceDate = null;
    body.iofNumber = null;
    // if (!body.batchNumber) {
    //   body.batchNumber = `RET-${Date.now()}`;
    // }
  }

  if (!(Number(body.defectiveQuantity) > 0)) {
    body.defectiveImages = null;
  }

  if (usesWarDefRefJournal(sourceType)) {
    body.war_def_ref_journal_id = toNumberOrNull(
      payload.warDefRefJournalId ?? payload.war_def_ref_journal_id
    );
  } else {
    body.war_def_ref_journal_id = null;
  }

  if (usesCustomerWarrantyReplacementType(sourceType)) {
    body.customerWarrantyReplacementType = emptyToNull(
      payload.customerWarrantyReplacementType
    );
  }

  return body;
}

function extractCustomerAllocationId(api) {
  if (!api || typeof api !== "object") return "";

  const direct = firstDefined(
    api.customerAllocationId,
    api.customer_allocation_id,
    api.allocationId,
    api.allocation_id,
    api.customerAllocationID,
    api.referenceId,
    api.reference_id,
    api.sourceId,
    api.source_id,
    api.documentId,
    api.document_id,
    api.linkedAllocationId,
    api.linked_allocation_id,
    api.reservationAllocationId,
    api.reservation_allocation_id
  );
  if (direct != null && direct !== "") return String(direct);

  for (const [key, value] of Object.entries(api)) {
    if (value == null || value === "" || typeof value === "object") continue;
    if (/customer.?allocation.?id/i.test(key) || /^allocationid$/i.test(key)) {
      return String(value);
    }
  }

  const reference = String(
    firstDefined(
      api.reference,
      api.referenceNumber,
      api.reference_number,
      api.documentReference,
      api.sourceReference,
      api.allocationReference,
      api.refNo,
      api.ref
    ) || ""
  );
  const match = reference.match(/ALC-?0*(\d+)/i);
  if (match) return match[1];
  if (/^\d+$/.test(reference.trim())) return reference.trim();

  return "";
}

/**
 * Normalize journal movement labels to match backend ALLOWED_MOVEMENT_TYPES.
 * Keeps legacy aliases readable for historical rows.
 */
function toUiMovementType(apiMovementType) {
  const raw = String(apiMovementType || "").trim();
  if (!raw) return "";
  const spaced = raw.replace(/_/g, " ").replace(/\s+/g, " ").trim();
  const key = spaced.toLowerCase();
  const legacyMap = {
    "return to vendor excess return": "Excess Return to Vendor",
    "return to vendor defective replacement":
      "Defective Replacement Request to Vendor",
    "return to vendor warranty replacement":
      "OEM Warranty Replacement Request to Vendor",
    "oem warranty replacement": "OEM Warranty Replacement Request to Vendor",
  };
  return legacyMap[key] || spaced;
}

/**
 * Map UI filter movement labels to the values stored / accepted by IMS05S.
 * Underscored transfer enums stay as-is; spaced labels are sent unchanged
 * (journal rows historically store both styles).
 */
function toApiMovementType(uiMovementType) {
  const raw = String(uiMovementType || "").trim();
  if (!raw || raw === "All") return null;
  const underscoredAliases = {
    "Customer Demo Inward": "Customer_Demo_Inward",
    "Customer Demo Outward": "Customer_Demo_Outward",
    "Material On Loan To Customer": "Material_On_Loan_To_Customer",
    "Warehouse To Warehouse": "Warehouse_To_Warehouse",
    "Advance Replacement To Customer": "Advance_Replacement_To_Customer",
    "Material On Loan To Vendor": "Material_On_Loan_To_Vendor",
    "Material Demo To Customer": "Customer_Demo_Outward",
  };
  return underscoredAliases[raw] || raw;
}

function mapStockJournalRow(api) {
  if (!api || typeof api !== "object") return null;

  const id = firstDefined(api.stockJournalId, api.stock_journal_id, api.id);
  const productId = firstDefined(api.productId, api.product_id);
  const warehouseLocationId = firstDefined(
    api.warehouseLocationId,
    api.warehouse_location_id,
    api.warehouseId,
    api.warehouse_id,
    api.sourceWarehouseId,
    api.source_warehouse_id,
    api.fromWarehouseId,
    api.from_warehouse_id,
    api.fromWarehouseLocationId,
    api.from_warehouse_location_id
  );
  const productName = firstDefined(api.productName, api.product_name);
  const fromName = firstDefined(api.fromName, api.from_name);
  const toName = firstDefined(api.toName, api.to_name);
  const warehouseName = firstDefined(
    api.warehouseName,
    api.warehouse_name,
    api.warehouseLocationName,
    api.warehouse_location_name,
    typeof api.warehouse === "string" ? api.warehouse : api.warehouse?.warehouseName,
    fromName
  );
  const vendorId = firstDefined(
    api.vendorId,
    api.vendor_id,
    api.vendor?.vendorId,
    api.vendor?.vendor_id,
    api.vendor?.id,
    api.toVendorId,
    api.to_vendor_id,
    api.toId,
    api.to_id
  );
  const vendorName = firstDefined(
    api.vendorName,
    api.vendor_name,
    api.vendor?.tradeName,
    api.vendor?.legalCompanyName,
    api.vendor?.name,
    toName
  );
  const movementType = toUiMovementType(
    firstDefined(api.movementType, api.movement_type)
  );

  const transferQuantityRaw = firstDefined(
    api.transferQuantity,
    api.transfer_quantity,
    api.quantity
  );
  const reservedQuantityRaw = firstDefined(
    api.reservedQuantity,
    api.reserved_quantity
  );
  const defectiveQuantityRaw = firstDefined(
    api.defectiveQuantity,
    api.defective_quantity,
    api.defectiveQty,
    api.defective_qty
  );
  const transferQuantity = Number(transferQuantityRaw);
  const reservedQuantity =
    reservedQuantityRaw != null && reservedQuantityRaw !== ""
      ? Number(reservedQuantityRaw)
      : null;
  const isDefectAdjustment =
    String(movementType || "").toLowerCase() === "defect adjustment";
  // Defect Adjustment posts qty on transferQuantity; surface it as defectiveQuantity.
  let defectiveQuantity = null;
  if (defectiveQuantityRaw != null && defectiveQuantityRaw !== "") {
    const n = Number(defectiveQuantityRaw);
    defectiveQuantity = Number.isFinite(n) ? n : null;
  } else if (isDefectAdjustment && Number.isFinite(transferQuantity)) {
    defectiveQuantity = transferQuantity;
  }

  const inventoryInId = firstDefined(
    api.inventoryInId,
    api.inventory_in_id,
    api.inventoryRecordId,
    api.inventory_record_id
  );

  return {
    id: id != null ? String(id) : `${productId}-${warehouseLocationId}-${api.date}`,
    stockJournalId: id ?? "",
    date: firstDefined(api.date, api.movementDate, api.movement_date) || null,
    movementType,
    productId: productId != null ? String(productId) : "",
    productName: displayOrId(productName, productId),
    partNumber: firstDefined(api.partNumber, api.part_number) || "",
    warehouseLocationId: warehouseLocationId != null ? String(warehouseLocationId) : "",
    warehouseName: displayOrId(warehouseName, warehouseLocationId),
    batchNumber: firstDefined(api.batchNumber, api.batch_number) || "—",
    quantity: Number.isFinite(transferQuantity) ? transferQuantity : 0,
    transferQuantity: Number.isFinite(transferQuantity) ? transferQuantity : 0,
    reservedQuantity:
      reservedQuantity != null && Number.isFinite(reservedQuantity)
        ? reservedQuantity
        : null,
    defectiveQuantity,
    purchasePricePerUnit: firstDefined(
      api.purchasePricePerUnit,
      api.purchase_price_per_unit
    ) ?? null,
    vendorId: vendorId != null ? String(vendorId) : "",
    vendorName: displayOrId(vendorName, vendorId),
    fromName: fromName || "—",
    toName: toName || "—",
    warrantyType: firstDefined(api.warrantyType, api.warranty_type) || null,
    remarks: emptyToNull(firstDefined(api.remarks, api.remark)) || "",
    customerAllocationId: extractCustomerAllocationId(api),
    inventoryInId:
      inventoryInId != null && String(inventoryInId).trim() !== ""
        ? String(inventoryInId)
        : "",
    raw: api,
  };
}

function mapStockPositionRow(api) {
  if (!api || typeof api !== "object") return null;

  const productId = firstDefined(api.productId, api.product_id);
  const warehouseLocationId = firstDefined(
    api.warehouseLocationId,
    api.warehouse_location_id,
    api.warehouseId,
    api.warehouse_id
  );
  const id = firstDefined(api.stockPositionId, api.stock_position_id, api.id);

  const productName = firstDefined(api.productName, api.product_name);
  const warehouseName = firstDefined(
    api.warehouseName,
    api.warehouse_name,
    typeof api.warehouse === "string" ? api.warehouse : api.warehouse?.warehouseName
  );

  const totalStock = toFiniteNumber(firstDefined(api.totalStock, api.total_stock));
  const reservedStock = toFiniteNumber(firstDefined(api.reservedStock, api.reserved_stock));
  const serviceSafetyStock = toFiniteNumber(
    firstDefined(
      api.serviceSafetyStock,
      api.service_safety_stock,
      api.maintenanceReserve,
      api.maintenance_reserve,
      api.maintenanceStock,
      api.maintenance_stock
    )
  );
  const salesSafetyStock = toFiniteNumber(
    firstDefined(api.salesSafetyStock, api.sales_safety_stock)
  );
  // Alias used by list filters / KPIs that still key off maintenanceReserve.
  const maintenanceReserve = serviceSafetyStock;
  const defectiveStock = toFiniteNumber(firstDefined(api.defectiveStock, api.defective_stock));
  const listPrice = toFiniteNumber(
    firstDefined(api.listPrice, api.list_price, api.costPrice, api.cost_price)
  );

  const serviceAvailableRaw = firstDefined(
    api.serviceAvailable,
    api.service_available
  );
  const salesAvailableRaw = firstDefined(api.salesAvailable, api.sales_available);
  const availableRaw = firstDefined(api.availableStock, api.available_stock);

  const serviceAvailable =
    serviceAvailableRaw != null && serviceAvailableRaw !== ""
      ? toFiniteNumber(serviceAvailableRaw)
      : availableRaw != null && availableRaw !== ""
        ? toFiniteNumber(availableRaw)
        : Math.max(0, totalStock - reservedStock);

  const salesAvailable =
    salesAvailableRaw != null && salesAvailableRaw !== ""
      ? toFiniteNumber(salesAvailableRaw)
      : Math.max(0, totalStock - reservedStock - serviceSafetyStock);

  // availableStock aliases serviceAvailable (backend contract).
  const availableStock =
    availableRaw != null && availableRaw !== ""
      ? toFiniteNumber(availableRaw)
      : serviceAvailable;

  const stockValueRaw = firstDefined(api.stockValue, api.stock_value, api.totalStockValue);
  const stockValue =
    stockValueRaw != null && stockValueRaw !== ""
      ? toFiniteNumber(stockValueRaw)
      : availableStock * listPrice;

  const serviceAvailableValueRaw = firstDefined(
    api.serviceAvailableValue,
    api.service_available_value
  );
  const salesAvailableValueRaw = firstDefined(
    api.salesAvailableValue,
    api.sales_available_value
  );
  const availableStockValueRaw = firstDefined(
    api.availableStockValue,
    api.available_stock_value
  );
  const serviceAvailableValue =
    serviceAvailableValueRaw != null && serviceAvailableValueRaw !== ""
      ? toFiniteNumber(serviceAvailableValueRaw)
      : availableStockValueRaw != null && availableStockValueRaw !== ""
        ? toFiniteNumber(availableStockValueRaw)
        : stockValue;
  const salesAvailableValue =
    salesAvailableValueRaw != null && salesAvailableValueRaw !== ""
      ? toFiniteNumber(salesAvailableValueRaw)
      : Math.max(0, serviceAvailableValue - serviceSafetyStock * listPrice);
  const availableStockValue =
    availableStockValueRaw != null && availableStockValueRaw !== ""
      ? toFiniteNumber(availableStockValueRaw)
      : serviceAvailableValue;

  const defectiveValueRaw = firstDefined(api.defectiveValue, api.defective_value);
  const defectiveValue =
    defectiveValueRaw != null && defectiveValueRaw !== ""
      ? toFiniteNumber(defectiveValueRaw)
      : defectiveStock * listPrice;

  const serviceSafetyStockValueRaw = firstDefined(
    api.serviceSafetyStockValue,
    api.service_safety_stock_value,
    api.maintenanceReserveValue,
    api.maintenance_reserve_value
  );
  const serviceSafetyStockValue =
    serviceSafetyStockValueRaw != null && serviceSafetyStockValueRaw !== ""
      ? toFiniteNumber(serviceSafetyStockValueRaw)
      : serviceSafetyStock * listPrice;
  const maintenanceReserveValue = serviceSafetyStockValue;

  const salesSafetyStockValueRaw = firstDefined(
    api.salesSafetyStockValue,
    api.sales_safety_stock_value
  );
  const salesSafetyStockValue =
    salesSafetyStockValueRaw != null && salesSafetyStockValueRaw !== ""
      ? toFiniteNumber(salesSafetyStockValueRaw)
      : salesSafetyStock * listPrice;

  const reservedStockValueRaw = firstDefined(
    api.reservedStockValue,
    api.reserved_stock_value
  );
  const reservedStockValue =
    reservedStockValueRaw != null && reservedStockValueRaw !== ""
      ? toFiniteNumber(reservedStockValueRaw)
      : reservedStock * listPrice;

  const lastOutwardMovementDate =
    firstDefined(
      api.lastOutwardMovementDate,
      api.last_outward_movement_date,
      api.lastOutwardDate
    ) || null;

  return {
    id: id != null ? String(id) : `${productId ?? "p"}-${warehouseLocationId ?? "w"}`,
    productId: productId != null ? String(productId) : "",
    productName: displayOrId(productName, productId),
    partNumber: firstDefined(api.partNumber, api.part_number) || "",
    systemName: firstDefined(api.systemName, api.system_name) || "",
    warehouseLocationId: warehouseLocationId != null ? String(warehouseLocationId) : "",
    warehouse: displayOrId(warehouseName, warehouseLocationId),
    category:
      firstDefined(api.category, api.productCategory, api.product_category) || "",
    totalStock,
    reservedStock,
    reservedStockValue,
    serviceSafetyStock,
    salesSafetyStock,
    serviceSafetyStockValue,
    salesSafetyStockValue,
    maintenanceReserve,
    maintenanceReserveValue,
    defectiveStock,
    defectiveValue,
    serviceAvailable,
    salesAvailable,
    serviceAvailableValue,
    salesAvailableValue,
    availableStock,
    listPrice,
    stockValue,
    availableStockValue,
    lastOutwardMovementDate,
    isDeadStock: resolveIsDeadStock(api, lastOutwardMovementDate),
    reorderQuantity: toFiniteNumber(
      firstDefined(api.reorderQuantity, api.reorder_quantity, api.reorderQty)
    ),
    pendingRevokes: toFiniteNumber(firstDefined(api.pendingRevokes, api.pending_revokes)),
    raw: api,
  };
}

function mapStockPositionBatch(api) {
  if (!api || typeof api !== "object") return null;

  const batchNumber = firstDefined(api.batchNumber, api.batch_number, api.batchId, api.batch_id);
  const totalQty = toFiniteNumber(
    firstDefined(api.totalQty, api.total_qty, api.totalQuantity, api.totalStock)
  );
  const acceptedQuantity = toFiniteNumber(
    firstDefined(
      api.acceptedQuantity,
      api.accepted_quantity,
      api.acceptedQty,
      api.accepted_qty,
      totalQty
    )
  );
  const reservedQty = toFiniteNumber(
    firstDefined(api.reservedQty, api.reserved_qty, api.reservedQuantity, api.reservedStock)
  );
  const defectiveQty = toFiniteNumber(
    firstDefined(api.defectiveQty, api.defective_qty, api.defectiveQuantity, api.defectiveStock)
  );
  // Prefer availableStock / availableQty (API fields on stock-position detail batches).
  const availableRaw = firstDefined(
    api.availableStock,
    api.available_stock,
    api.availableQty,
    api.available_qty,
    api.availableQuantity
  );
  // Safety stock is warehouse-level only — batch free qty is total − reserved (− defective when present).
  const freeQty =
    availableRaw != null && availableRaw !== ""
      ? toFiniteNumber(availableRaw)
      : Math.max(0, totalQty - reservedQty - defectiveQty);

  const serviceAvailableRaw = firstDefined(
    api.serviceAvailable,
    api.service_available
  );
  const salesAvailableRaw = firstDefined(api.salesAvailable, api.sales_available);
  const serviceAvailable =
    serviceAvailableRaw != null && serviceAvailableRaw !== ""
      ? toFiniteNumber(serviceAvailableRaw)
      : freeQty;
  const salesAvailable =
    salesAvailableRaw != null && salesAvailableRaw !== ""
      ? toFiniteNumber(salesAvailableRaw)
      : freeQty;

  const availableStock = freeQty;
  const purchasePricePerUnit = toFiniteNumber(
    firstDefined(
      api.purchasePricePerUnit,
      api.purchase_price_per_unit,
      api.unitPrice,
      api.unit_price,
      api.purchasePrice
    )
  );

  return {
    batchNumber: batchNumber != null ? String(batchNumber) : "—",
    receiptDate: firstDefined(api.receiptDate, api.receipt_date) || null,
    warrantyStart:
      firstDefined(api.warrantyStart, api.warranty_start, api.warrantyStartDate) || null,
    warrantyEnd:
      firstDefined(
        api.warrantyEnd,
        api.warranty_end,
        api.warrantyEndDate,
        api.warranty_end_date
      ) || null,
    totalQty,
    acceptedQuantity,
    reservedQty,
    defectiveQty,
    serviceAvailable,
    salesAvailable,
    availableStock,
    availableQty: availableStock,
    purchasePricePerUnit,
    status: toUiBatchStatus(firstDefined(api.status, api.batchStatus, api.batch_status)),
  };
}

function mapStockPositionWarehouse(api, listPrice = 0) {
  if (!api || typeof api !== "object") return null;

  const nested = api.warehouse && typeof api.warehouse === "object" ? api.warehouse : {};
  const warehouseId = firstDefined(
    api.warehouseId,
    api.warehouse_id,
    api.warehouseLocationId,
    api.warehouse_location_id,
    nested.warehouseId,
    nested.id
  );
  const warehouseName = firstDefined(
    api.warehouseName,
    api.warehouse_name,
    typeof api.warehouse === "string" ? api.warehouse : null,
    nested.warehouseName,
    nested.name
  );

  const totalStock = toFiniteNumber(firstDefined(api.totalStock, api.total_stock, api.totalQty));
  const reservedStock = toFiniteNumber(
    firstDefined(api.reservedStock, api.reserved_stock, api.reservedQty)
  );
  const serviceSafetyStock = toFiniteNumber(
    firstDefined(
      api.serviceSafetyStock,
      api.service_safety_stock,
      api.maintenanceReserve,
      api.maintenance_reserve,
      api.maintenanceStock
    )
  );
  const salesSafetyStock = toFiniteNumber(
    firstDefined(api.salesSafetyStock, api.sales_safety_stock)
  );
  const maintenanceReserve = serviceSafetyStock;
  const defectiveStock = toFiniteNumber(
    firstDefined(api.defectiveStock, api.defective_stock, api.defectiveQty)
  );

  const serviceAvailableRaw = firstDefined(
    api.serviceAvailable,
    api.service_available
  );
  const salesAvailableRaw = firstDefined(api.salesAvailable, api.sales_available);
  const availableRaw = firstDefined(api.availableStock, api.available_stock);

  const serviceAvailable =
    serviceAvailableRaw != null && serviceAvailableRaw !== ""
      ? toFiniteNumber(serviceAvailableRaw)
      : availableRaw != null && availableRaw !== ""
        ? toFiniteNumber(availableRaw)
        : Math.max(0, totalStock - reservedStock);

  const salesAvailable =
    salesAvailableRaw != null && salesAvailableRaw !== ""
      ? toFiniteNumber(salesAvailableRaw)
      : Math.max(0, totalStock - reservedStock - serviceSafetyStock);

  const availableStock =
    availableRaw != null && availableRaw !== ""
      ? toFiniteNumber(availableRaw)
      : serviceAvailable;

  const stockValueRaw = firstDefined(api.stockValue, api.stock_value);
  const stockValue =
    stockValueRaw != null && stockValueRaw !== ""
      ? toFiniteNumber(stockValueRaw)
      : availableStock * listPrice;

  const batchesRaw = firstDefined(api.batches, api.batchList, api.batchInventory, api.batch_list);
  const batches = Array.isArray(batchesRaw)
    ? batchesRaw.map(mapStockPositionBatch).filter(Boolean)
    : [];

  const serviceAvailableValueRaw = firstDefined(
    api.serviceAvailableValue,
    api.service_available_value
  );
  const salesAvailableValueRaw = firstDefined(
    api.salesAvailableValue,
    api.sales_available_value
  );
  const availableStockValueRaw = firstDefined(
    api.availableStockValue,
    api.available_stock_value
  );
  const serviceAvailableValue =
    serviceAvailableValueRaw != null && serviceAvailableValueRaw !== ""
      ? toFiniteNumber(serviceAvailableValueRaw)
      : availableStockValueRaw != null && availableStockValueRaw !== ""
        ? toFiniteNumber(availableStockValueRaw)
        : availableStock * listPrice;
  const salesAvailableValue =
    salesAvailableValueRaw != null && salesAvailableValueRaw !== ""
      ? toFiniteNumber(salesAvailableValueRaw)
      : Math.max(0, serviceAvailableValue - serviceSafetyStock * listPrice);
  const availableStockValue =
    availableStockValueRaw != null && availableStockValueRaw !== ""
      ? toFiniteNumber(availableStockValueRaw)
      : serviceAvailableValue;

  const defectiveValueRaw = firstDefined(api.defectiveValue, api.defective_value);
  const defectiveValueFromBatches = batches.reduce((sum, batch) => {
    const unitPrice = Number(batch.purchasePricePerUnit) || listPrice || 0;
    return sum + (Number(batch.defectiveQty) || 0) * unitPrice;
  }, 0);
  const defectiveValue =
    defectiveValueRaw != null && defectiveValueRaw !== ""
      ? toFiniteNumber(defectiveValueRaw)
      : defectiveValueFromBatches > 0
        ? defectiveValueFromBatches
        : defectiveStock * listPrice;

  return {
    warehouseId: warehouseId != null ? String(warehouseId) : warehouseName || "unknown",
    warehouseName: displayOrId(warehouseName, warehouseId),
    totalStock,
    reservedStock,
    serviceSafetyStock,
    salesSafetyStock,
    maintenanceReserve,
    defectiveStock,
    serviceAvailable,
    salesAvailable,
    serviceAvailableValue,
    salesAvailableValue,
    availableStock,
    stockValue,
    availableStockValue,
    defectiveValue,
    batches,
  };
}

function mapStockPositionDetail(api) {
  if (!api || typeof api !== "object") return { product: null, warehouses: [] };

  const productSrc =
    api.product && typeof api.product === "object" && !Array.isArray(api.product)
      ? api.product
      : api;

  const productId = firstDefined(productSrc.productId, productSrc.product_id, api.productId);
  const listPrice = toFiniteNumber(
    firstDefined(
      productSrc.listPrice,
      productSrc.list_price,
      productSrc.costPrice,
      productSrc.cost_price,
      api.listPrice
    )
  );
  const statusRaw = firstDefined(
    productSrc.status,
    productSrc.productStatus,
    productSrc.product_status
  );

  const product = {
    productId: productId != null ? String(productId) : "",
    productName: displayOrId(
      firstDefined(productSrc.productName, productSrc.product_name, productSrc.name),
      productId
    ),
    partNumber: firstDefined(productSrc.partNumber, productSrc.part_number) || "",
    manufacturer:
      firstDefined(
        productSrc.manufacturer,
        productSrc.productBrand,
        productSrc.product_brand,
        productSrc.brand
      ) || "—",
    category:
      firstDefined(productSrc.category, productSrc.productCategory, productSrc.product_category) ||
      "—",
    uom:
      firstDefined(
        productSrc.uom,
        productSrc.unitOfMeasure,
        productSrc.unit_of_measure,
        productSrc.metricName,
        productSrc.unit
      ) || "—",
    listPrice,
    status: toUiProductStatus(statusRaw, firstDefined(productSrc.isActive, productSrc.is_active)),
  };

  const warehousesRaw = firstDefined(
    api.warehouses,
    api.warehouseInventory,
    api.warehouse_inventory,
    api.warehouseStocks,
    api.stockByWarehouse,
    api.warehousePositions,
    api.locations,
    productSrc.warehouses
  );
  const warehouses = Array.isArray(warehousesRaw)
    ? warehousesRaw
        .map((row) => mapStockPositionWarehouse(row, listPrice))
        .filter(Boolean)
    : [];

  return { product, warehouses };
}

function toAllocationHeaderStatus(status) {
  const raw = String(status || "").trim();
  if (!raw) return "Reserved";
  const key = raw.toUpperCase().replace(/[\s-]+/g, "_");
  if (key === "RESERVED") return "Reserved";
  if (key === "ALLOCATED") return "Allocated";
  if (key === "ELIGIBLE") return "Eligible";
  if (
    key === "PARTIALLY_ALLOCATED" ||
    key === "PARTIAL" ||
    key === "SHORTFALL"
  ) {
    return "Partially Allocated";
  }
  if (key === "PARTIALLY_DELIVERED") return "Partially Delivered";
  if (key === "PENDING_APPROVAL") return "Pending Approval";
  if (key === "DELIVERED") return "Delivered";
  if (key === "REVOKED") return "Revoked";
  // Normalize underscore/space variants the API may send
  if (key.includes("_")) {
    return key
      .toLowerCase()
      .split("_")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  }
  return raw;
}

function toAllocationLineStatus(status) {
  const raw = String(status || "").trim();
  if (!raw) return "Eligible";
  const key = raw.toUpperCase().replace(/[\s-]+/g, "_");
  if (key === "SHORTFALL") return "Shortfall";
  if (key === "ELIGIBLE") return "Eligible";
  if (key === "ALLOCATED") return "Allocated";
  if (key === "RESERVED") return "Reserved";
  if (key === "PARTIALLY_ALLOCATED" || key === "PARTIAL") {
    return "Partially Allocated";
  }
  if (key === "BLOCKED" || key === "BLOCKED_BY_PRICE") return "Blocked";
  if (key === "PARTIALLY_DELIVERED") return "Partially Delivered";
  if (key === "PENDING_APPROVAL") return "Pending Approval";
  if (key === "DELIVERED") return "Delivered";
  if (key === "REVOKED") return "Revoked";
  if (key.includes("_")) {
    return key
      .toLowerCase()
      .split("_")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  }
  return raw;
}

/** Normalize API/UI dates to YYYY-MM-DD for IMS payloads. */
function toIsoDateString(val) {
  if (val == null || val === "") return null;
  const raw = String(val).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const dateMatch = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (dateMatch) return dateMatch[1];
  const d = new Date(raw);
  if (!Number.isFinite(d.getTime())) return null;
  return d.toISOString().slice(0, 10);
}

function formatAllocationReference(id) {
  if (id == null || id === "") return "—";
  const numeric = String(id).replace(/\D/g, "");
  if (numeric) return `ALC-${numeric.padStart(4, "0")}`;
  return String(id);
}

/** Pass backend lineStatuses through as-is (backend already sends distinct values). */
function mapLineStatuses(api) {
  if (!api || typeof api !== "object") return [];

  let raw = firstDefined(
    api.lineStatuses,
    api.line_statuses,
    api.lineStatusList,
    api.distinctLineStatuses,
    api.statuses
  );

  // Some payloads serialize the array as a JSON / comma-separated string
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) {
      raw = null;
    } else if (trimmed.startsWith("[")) {
      try {
        raw = JSON.parse(trimmed);
      } catch {
        raw = trimmed.split(",").map((s) => s.trim());
      }
    } else {
      raw = trimmed.split(",").map((s) => s.trim());
    }
  }

  if (Array.isArray(raw) && raw.length > 0) {
    return raw.map((s) => String(s ?? "").trim()).filter(Boolean);
  }

  // Fallback if search returns lines[] without a top-level lineStatuses field
  const lines = firstDefined(api.lines, api.allocationLines, api.products);
  if (Array.isArray(lines) && lines.length > 0) {
    const seen = new Set();
    const out = [];
    for (const line of lines) {
      const status = String(
        firstDefined(line?.status, line?.lineStatus, line?.line_status) || ""
      ).trim();
      if (!status || seen.has(status)) continue;
      seen.add(status);
      out.push(status);
    }
    return out;
  }

  return [];
}

function mapCustomerAllocationListRow(api) {
  if (!api || typeof api !== "object") return null;

  const allocationId = firstDefined(
    api.customerAllocationId,
    api.customer_allocation_id,
    api.allocationId,
    api.id
  );
  if (allocationId == null || allocationId === "") return null;

  const lineStatuses = mapLineStatuses(api);

  return {
    id: String(allocationId),
    allocationId: String(allocationId),
    reference:
      firstDefined(api.reference, api.allocationReference, api.allocationRef) ||
      formatAllocationReference(allocationId),
    date: firstDefined(api.orderDate, api.date, api.createdAt) || null,
    customerName: firstDefined(api.customerName, api.customer_name) || "—",
    customerId: firstDefined(api.customerId, api.customer_id) ?? null,
    orderType:
      firstDefined(
        api.allocationType,
        api.allocation_type,
        api.orderType,
        api.order_type,
        api.salesServiceType
      ) || "Sales",
    allocationType:
      firstDefined(
        api.allocationType,
        api.allocation_type,
        api.orderType,
        api.order_type,
        api.salesServiceType
      ) || "Sales",
    projectName: firstDefined(api.projectName, api.project_name) || "—",
    invoiceNumber:
      firstDefined(api.customerInvoiceNumber, api.invoiceNumber, api.customerInvoiceId) ||
      "—",
    iofNumber: firstDefined(api.iofNumber, api.iof_number) || "—",
    dispatchMethod: firstDefined(api.dispatchMethod, api.allocationMethod) || "—",
    deliveryDate: firstDefined(api.deliveryDate, api.delivery_date) || null,
    status: toAllocationHeaderStatus(firstDefined(api.status, api.allocationStatus)),
    lineStatuses,
    linesCount: toFiniteNumber(
      firstDefined(api.linesCount, api.lineCount, api.productsCount, api.totalLines)
    ),
    raw: api,
  };
}

function mapCustomerAllocationDetail(api) {
  if (!api || typeof api !== "object") return null;

  const allocationId = firstDefined(
    api.customerAllocationId,
    api.customer_allocation_id,
    api.allocationId,
    api.id
  );
  const reference =
    firstDefined(api.reference, api.allocationReference, api.allocationRef) ||
    formatAllocationReference(allocationId);

  const linesRaw = firstDefined(api.lines, api.allocationLines, api.products);
  const products = (Array.isArray(linesRaw) ? linesRaw : []).map((line, index) => {
    const detailsRaw = firstDefined(line.details, line.batches, line.priceOverrides, line.batchDetails);
    const batches = (Array.isArray(detailsRaw) ? detailsRaw : []).map((d) => {
      const detailId = firstDefined(
        d.customerAllocationLineDetailId,
        d.customer_allocation_line_detail_id,
        d.customerAllocationDetailId,
        d.customer_allocation_detail_id,
        d.id
      );
      return {
        customerAllocationLineDetailId:
          detailId != null && detailId !== "" ? String(detailId) : "",
        batchNumber: firstDefined(d.batchNumber, d.batch_number) || "—",
        warehouseId: firstDefined(d.warehouseId, d.warehouse_id) ?? null,
        warehouse:
          firstDefined(d.warehouseName, d.warehouse_name, d.warehouse) ||
          displayOrId(null, firstDefined(d.warehouseId, d.warehouse_id)),
        purchasePricePerUnit: toFiniteNumber(
          firstDefined(d.purchasePricePerUnit, d.purchase_price_per_unit, d.pricePerUnit)
        ),
        allocatedQty: toFiniteNumber(
          firstDefined(d.allocatedQuantity, d.allocated_quantity)
        ),
        unlockedPrice:
          firstDefined(d.unlockedPrice, d.unlocked_price) != null &&
          firstDefined(d.unlockedPrice, d.unlocked_price) !== ""
            ? toFiniteNumber(firstDefined(d.unlockedPrice, d.unlocked_price))
            : null,
        unlockStatus: String(
          firstDefined(
            d.unlockStatus,
            d.unlock_status,
            d.priceUnlockStatus,
            d.price_unlock_status,
            d.unlockRequestStatus,
            d.unlock_request_status
          ) || ""
        )
          .trim()
          .toUpperCase() || null,
        status: toAllocationLineStatus(
          firstDefined(d.status, d.detailStatus, d.lineDetailStatus)
        ),
        warrantyEndDate:
          firstDefined(d.warrantyEndDate, d.warranty_end_date, d.warrantyEnd) || null,
        overrideReason:
          firstDefined(d.overrideReason, d.override_reason) || null,
      };
    });

    const allocatedQty = batches.reduce((sum, b) => sum + (Number(b.allocatedQty) || 0), 0);
    const requestedQty = toFiniteNumber(
      firstDefined(line.iofQuantity, line.iof_quantity, line.requestedQty, line.iofQty)
    );
    const shortfallRaw = firstDefined(line.shortfall, line.shortfallQty);
    const shortfall =
      shortfallRaw != null && shortfallRaw !== ""
        ? toFiniteNumber(shortfallRaw)
        : Math.max(0, requestedQty - allocatedQty);

    const metricId = firstDefined(line.metricId, line.metric_id);
    const rawUom = firstDefined(
      line.metricName,
      line.metric_name,
      line.unitOfMeasure,
      line.unit_of_measure,
      line.uom
    );
    const customerAllocationLineId = firstDefined(
      line.customerAllocationLineId,
      line.customer_allocation_line_id,
      line.id
    );
    const lineDeliveryDate =
      toIsoDateString(firstDefined(line.deliveryDate, line.delivery_date)) || null;

    return {
      id: String(
        firstDefined(customerAllocationLineId, line.productId, index)
      ),
      customerAllocationLineId:
        customerAllocationLineId != null && customerAllocationLineId !== ""
          ? String(customerAllocationLineId)
          : "",
      productId: firstDefined(line.productId, line.product_id) != null
        ? String(firstDefined(line.productId, line.product_id))
        : "",
      name:
        firstDefined(line.productName, line.product_name, line.name) ||
        displayOrId(null, line.productId),
      partNumber: firstDefined(line.partNumber, line.part_number) || "—",
      manufacturer:
        firstDefined(line.manufacturer, line.productBrand, line.product_brand) || "",
      metricId: metricId != null && metricId !== "" ? String(metricId) : "",
      uom: rawUom != null && String(rawUom).trim() !== "" ? String(rawUom) : "—",
      requestedQty,
      iofQuantity: requestedQty,
      allocatedQty:
        firstDefined(line.allocatedQuantity, line.allocatedQty) != null
          ? toFiniteNumber(firstDefined(line.allocatedQuantity, line.allocatedQty))
          : allocatedQty,
      shortfall,
      blockedByPriceQty: toFiniteNumber(
        firstDefined(line.blockedByPrice, line.blockedByPriceQty, line.blockedQuantity)
      ),
      consideredCostPerUnit: toFiniteNumber(
        firstDefined(
          line.consideredCostPerUnit,
          line.considered_cost_per_unit,
          line.consideredCost
        )
      ),
      allocationMethod:
        firstDefined(
          line.dispatchMethod,
          line.allocationMethod,
          api.dispatchMethod,
          api.allocationMethod
        ) || "FIFO",
      deliveryDate: lineDeliveryDate,
      invoiceNumber:
        firstDefined(
          line.customerInvoiceNumber,
          line.customer_invoice_number,
          line.invoiceNumber,
          line.invoice_number
        ) || "",
      deliveryNotes:
        firstDefined(
          line.deliveryNotes,
          line.delivery_notes,
          line.notes,
          line.remarks
        ) || "",
      status: toAllocationLineStatus(
        firstDefined(
          line.status,
          lineDeliveryDate ? "Delivered" : null
        )
      ),
      batches,
    };
  });

  const requestedQty = products.reduce((sum, p) => sum + (Number(p.requestedQty) || 0), 0);
  const allocatedQty = products.reduce((sum, p) => sum + (Number(p.allocatedQty) || 0), 0);
  const shortfallQty = products.reduce((sum, p) => sum + (Number(p.shortfall) || 0), 0);
  const warehouses = new Set(
    products.flatMap((p) => (p.batches || []).map((b) => b.warehouse).filter(Boolean))
  );

  return {
    header: {
      allocationId: allocationId != null ? String(allocationId) : "",
      reference,
      status: toAllocationHeaderStatus(api.status),
      lineStatuses: mapLineStatuses(api),
      orderType:
        firstDefined(
          api.allocationType,
          api.allocation_type,
          api.orderType,
          api.order_type,
          api.salesServiceType
        ) || "Sales",
      allocationType:
        firstDefined(
          api.allocationType,
          api.allocation_type,
          api.orderType,
          api.order_type,
          api.salesServiceType
        ) || "Sales",
      date: firstDefined(api.orderDate, api.date, api.createdAt) || null,
      customerName: firstDefined(api.customerName, api.customer_name) || "—",
      customerId: firstDefined(api.customerId, api.customer_id) ?? null,
      projectName:
        firstDefined(api.projectName, api.project_name) || "—",
      invoiceNumber:
        firstDefined(api.customerInvoiceNumber, api.invoiceNumber) || "—",
      invoiceDate: firstDefined(api.customerInvoiceDate, api.invoiceDate) || null,
      deliveryDate: firstDefined(api.deliveryDate, api.delivery_date) || null,
      iofNumber: firstDefined(api.iofNumber, api.iof_number) || "—",
      dispatchMethod: firstDefined(api.dispatchMethod, api.allocationMethod) || "FIFO",
      createdBy: firstDefined(api.createdBy, api.created_by) || "—",
      createdOn: firstDefined(api.createdAt, api.createdOn) || null,
    },
    products,
    summary: {
      productsCount: products.length,
      requestedQty,
      allocatedQty,
      shortfallQty,
      allocationStatus: toAllocationHeaderStatus(api.status),
      lineStatuses: mapLineStatuses(api),
      numWarehouses: warehouses.size,
    },
  };
}

function mapInventoryReceiptPayload(payload = {}) {
  const sourceType = toApiSourceType(payload.sourceType);
  const defectiveQuantity =
    toNumberOrNull(payload.defectiveQuantity ?? payload.defectiveQty) ?? 0;
  const body = {
    sourceType,
    productId: toNumberOrNull(payload.productId),
    warehouseLocationId: toNumberOrNull(
      payload.warehouseLocationId ?? payload.warehouseId
    ),
    receivedQuantity: toNumberOrNull(payload.receivedQuantity ?? payload.receivedQty) ?? 0,
    defectiveQuantity,
    purchasePricePerUnit: toNumberOrNull(
      payload.purchasePricePerUnit ?? payload.purchasePrice
    ),
    batchNumber: emptyToNull(payload.batchNumber),
    warrantyStartDate: emptyToNull(payload.warrantyStartDate),
    warrantyEndDate: emptyToNull(payload.warrantyEndDate),
    remarks: emptyToNull(payload.remarks),
    inventoryImages: normalizeImagesPayload(
      payload.inventoryImages ?? payload.inventory_images
    ),
    defectiveImages: normalizeImagesPayload(
      payload.defectiveImages ?? payload.defective_images
    ),
  };

  if (usesVendorApiSourceType(sourceType)) {
    body.receiptDate = emptyToNull(payload.receiptDate);
    body.vendorId = toNumberOrNull(payload.vendorId);
    body.poNumber = emptyToNull(payload.poNumber);
    body.vendorInvoiceNumber = emptyToNull(
      payload.vendorInvoiceNumber ?? payload.invoiceNumber
    );
    body.invoiceDate = emptyToNull(payload.invoiceDate);
    body.iofNumber = emptyToNull(payload.iofNumber);
  } else {
    const returnDate = emptyToNull(payload.returnDate);
    body.returnDate = returnDate;
    // Backend currently expects receiptDate as well — mirror return date.
    body.receiptDate = emptyToNull(payload.receiptDate) || returnDate;
    body.customerId = toNumberOrNull(payload.customerId);
    body.customerInvoiceId = emptyToNull(payload.customerInvoiceId);
    body.projectName = emptyToNull(payload.projectName);
    body.batchNumber = emptyToNull(payload.batchNumber);
    // if (!body.batchNumber) {
    //   body.batchNumber = emptyToNull(payload.batchNumber) || `RET-${Date.now()}`;
    // }
  }

  if (!(Number(defectiveQuantity) > 0)) {
    body.defectiveImages = null;
  }

  if (usesWarDefRefJournal(sourceType)) {
    body.war_def_ref_journal_id = toNumberOrNull(
      payload.warDefRefJournalId ?? payload.war_def_ref_journal_id
    );
  }

  if (usesCustomerWarrantyReplacementType(sourceType)) {
    body.customerWarrantyReplacementType = emptyToNull(
      payload.customerWarrantyReplacementType
    );
  }

  return body;
}

function normalizePartyName(value) {
  const name = String(value || "").trim().toLowerCase();
  return !name || name === "—" || name === "-" ? "" : name;
}

/**
 * Qty from a journal row that should affect the given warehouse's on-hand stock.
 * Transfer Out belongs to fromName; Transfer In belongs to toName. Other movements
 * trust the warehouseLocationId filter on the search API.
 */
function journalQtyForWarehouse(row, warehouseName) {
  const qty = Number(row?.quantity ?? row?.transferQuantity) || 0;
  const type = String(row?.movementType || "")
    .replace(/_/g, " ")
    .trim()
    .toLowerCase();
  const wh = normalizePartyName(warehouseName);
  if (!wh) return qty;

  if (
    type === "transfer out" ||
    type === "customer demo outward" ||
    type === "material on loan to customer"
  ) {
    const from = normalizePartyName(row?.fromName);
    // Exclude the destination warehouse's copy of the outbound leg.
    if (from && from !== wh) return 0;
    return qty;
  }
  if (type === "transfer in" || type === "customer demo inward") {
    const to = normalizePartyName(row?.toName);
    // Exclude the source warehouse's copy of the inbound leg.
    if (to && to !== wh) return 0;
    return qty;
  }
  return qty;
}

function mapWarehouseLocationInventoryRow(api, page, index) {
  if (!api || typeof api !== "object") return null;
  const productId = firstDefined(api.productId, api.product_id);
  if (productId == null || productId === "") return null;
  const productName =
    firstDefined(api.productName, api.product_name) || displayOrId(null, productId);
  const batchRaw = firstDefined(api.batchNumber, api.batch_number);
  const batchNumber = batchRaw == null ? "" : String(batchRaw).trim();
  const priceRaw = firstDefined(api.purchasePricePerUnit, api.purchase_price_per_unit);
  const priceNumber =
    priceRaw == null || priceRaw === "" || !Number.isFinite(Number(priceRaw))
      ? null
      : Number(priceRaw);
  const invoiceRaw = firstDefined(api.vendorInvoiceNumber, api.vendor_invoice_number);
  const vendorInvoiceNumber =
    invoiceRaw == null || String(invoiceRaw).trim() === ""
      ? null
      : String(invoiceRaw).trim();
  return {
    id: String(productId),
    name: productName,
    productId: String(productId),
    productName,
    batchNumber,
    purchasePricePerUnit: priceNumber,
    vendorInvoiceNumber,
    warehouseLocationId:
      firstDefined(api.warehouseLocationId, api.warehouse_location_id) ?? null,
    customerInvoiceId:
      firstDefined(api.customerInvoiceId, api.customer_invoice_id) ?? null,
    rowKey: [
      page,
      index,
      productId,
      batchNumber,
      priceNumber ?? "",
      vendorInvoiceNumber ?? "",
      firstDefined(api.customerInvoiceId, api.customer_invoice_id) ?? "",
    ].join("::"),
  };
}

function toChallanDate(value) {
  const raw = String(value || "").trim();
  const match = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : raw || null;
}

function mapDeliveryChallanRow(api) {
  if (!api || typeof api !== "object") return null;
  const deliveryChallanId = toNumberOrNull(
    firstDefined(api.deliveryChallanId, api.delivery_challan_id)
  );
  if (deliveryChallanId == null) return null;

  const invoiceRaw = firstDefined(api.invoiceNumber, api.invoice_number);
  const invoiceNumber =
    invoiceRaw == null || String(invoiceRaw).trim() === ""
      ? ""
      : String(invoiceRaw).trim();

  let contentItems = [];
  const rawItems = firstDefined(api.contentItems, api.content_items);
  if (Array.isArray(rawItems)) {
    contentItems = rawItems;
  } else if (typeof rawItems === "string" && rawItems.trim()) {
    try {
      const parsed = JSON.parse(rawItems);
      contentItems = Array.isArray(parsed) ? parsed : [];
    } catch {
      contentItems = [];
    }
  }

  return {
    deliveryChallanId,
    challanDate: toChallanDate(firstDefined(api.challanDate, api.challan_date)),
    movementType: firstDefined(api.movementType, api.movement_type) || "",
    stockJournalId: toNumberOrNull(
      firstDefined(api.stockJournalId, api.stock_journal_id)
    ),
    challanTo: (() => {
      const raw = firstDefined(api.challanTo, api.challan_to);
      if (raw == null || String(raw).trim() === "") return null;
      const asNumber = toNumberOrNull(raw);
      return asNumber != null && String(asNumber) === String(raw).trim()
        ? asNumber
        : String(raw).trim();
    })(),
    challanToAddress:
      emptyToNull(
        firstDefined(api.challanToAddress, api.challan_to_address)
      ) || "",
    challanToMobile:
      emptyToNull(
        firstDefined(api.challanToMobile, api.challan_to_mobile)
      ) || "",
    customerAllocationId: toNumberOrNull(
      firstDefined(api.customerAllocationId, api.customer_allocation_id)
    ),
    invoiceNumber,
    deliveryContent:
      firstDefined(api.deliveryContent, api.delivery_content) || "",
    contentItems,
  };
}

function mapDeliveryChallanUpdatePayload(row) {
  const invoiceNumber = String(row?.invoiceNumber ?? "").trim();
  if (!invoiceNumber) {
    throw new Error("Invoice number is required");
  }
  return {
    challanDate: toChallanDate(row?.challanDate),
    movementType: row?.movementType || null,
    stockJournalId: toNumberOrNull(row?.stockJournalId),
    challanTo: toNumberOrNull(row?.challanTo),
    customerAllocationId: toNumberOrNull(row?.customerAllocationId),
    invoiceNumber,
    deliveryContent: row?.deliveryContent ?? "",
    contentItems: Array.isArray(row?.contentItems) ? row.contentItems : [],
  };
}

export const inventoryManagementService = {
  /** GET /saas/ims/stock-position/search — IMS05S */
  getStockPositions: async (filters = {}) => {
    const params = new URLSearchParams();
    params.set("page", String(filters.page ?? 0));
    params.set("size", String(filters.size ?? 10));
    params.set("sortBy", String(filters.sortBy || "productName"));
    params.set("sortDir", String(filters.sortDir || "ASC"));

    const productId = toNumberOrNull(
      filters.productId && filters.productId !== "All" ? filters.productId : null
    );
    if (productId != null) params.set("productId", String(productId));

    const warehouseLocationId = toNumberOrNull(
      filters.warehouseLocationId && filters.warehouseLocationId !== "All"
        ? filters.warehouseLocationId
        : filters.warehouse && filters.warehouse !== "All"
          ? filters.warehouse
          : null
    );
    if (warehouseLocationId != null) {
      params.set("warehouseLocationId", String(warehouseLocationId));
    }

    if (filters.category && filters.category !== "All") {
      params.set("category", String(filters.category));
    }

    const otherFilter = normalizeStockPositionOtherFilter(
      filters.otherFilter || filters.deadStock
    );

    // Dead stock is filtered server-side via deadStock=DEAD (IMS05S)
    if (otherFilter === POSITION_OTHER_FILTER.DEAD_STOCK) {
      params.set("deadStock", "DEAD");
    }

    const response = await imsGet(
      `/stock-position/search?${params.toString()}`,
      STOCK_POSITION_SEARCH
    );
    const page = unwrapPage(response, filters);
    let content = (page.content || []).map(mapStockPositionRow).filter(Boolean);

    // Client-side filters for Other Filters that are not yet supported by the API
    if (otherFilter === POSITION_OTHER_FILTER.DEFECTIVE) {
      content = content.filter((row) => (Number(row.defectiveStock) || 0) > 0);
    } else if (otherFilter === POSITION_OTHER_FILTER.SERVICE_SAFETY) {
      content = content.filter(
        (row) =>
          (Number(row.serviceSafetyStock ?? row.maintenanceReserve) || 0) > 0
      );
    } else if (otherFilter === POSITION_OTHER_FILTER.SALES_SAFETY) {
      content = content.filter((row) => (Number(row.salesSafetyStock) || 0) > 0);
    } else if (otherFilter === POSITION_OTHER_FILTER.RESERVED) {
      content = content.filter((row) => (Number(row.reservedStock) || 0) > 0);
    }

    const systemSummaries = (
      Array.isArray(page.systemSummaries) ? page.systemSummaries : []
    )
      .map((item) => {
        if (!item || typeof item !== "object") return null;
        const systemName = firstDefined(item.systemName, item.system_name) || "";
        return {
          systemName,
          total: toFiniteNumber(firstDefined(item.total, item.availableStockValue)),
        };
      })
      .filter(Boolean);

    // When Other Filters narrow rows client-side, rebuild system summaries from filtered content
    const filteredSystemSummaries =
      otherFilter === POSITION_OTHER_FILTER.DEFECTIVE ||
      otherFilter === POSITION_OTHER_FILTER.SERVICE_SAFETY ||
      otherFilter === POSITION_OTHER_FILTER.SALES_SAFETY ||
      otherFilter === POSITION_OTHER_FILTER.RESERVED
        ? (() => {
            const bySystem = new Map();
            content.forEach((row) => {
              const key = row.systemName || "—";
              let value = Number(row.availableStockValue) || 0;
              if (otherFilter === POSITION_OTHER_FILTER.DEFECTIVE) {
                value = Number(row.defectiveValue) || 0;
              } else if (otherFilter === POSITION_OTHER_FILTER.SERVICE_SAFETY) {
                value =
                  Number(
                    row.serviceSafetyStockValue ?? row.maintenanceReserveValue
                  ) || 0;
              } else if (otherFilter === POSITION_OTHER_FILTER.SALES_SAFETY) {
                value = Number(row.salesSafetyStockValue) || 0;
              } else if (otherFilter === POSITION_OTHER_FILTER.RESERVED) {
                value = Number(row.reservedStockValue) || 0;
              }
              bySystem.set(key, (bySystem.get(key) || 0) + value);
            });
            return Array.from(bySystem.entries()).map(([systemName, total]) => ({
              systemName,
              total,
            }));
          })()
        : systemSummaries;

    const availableStockValueTotalRaw = firstDefined(page.total, page.availableStockValueTotal);
    let availableStockValueTotal =
      availableStockValueTotalRaw != null && availableStockValueTotalRaw !== ""
        ? toFiniteNumber(availableStockValueTotalRaw)
        : systemSummaries.reduce((sum, row) => sum + (Number(row.total) || 0), 0);

    if (otherFilter === POSITION_OTHER_FILTER.DEFECTIVE) {
      availableStockValueTotal = content.reduce(
        (sum, row) => sum + (Number(row.defectiveValue) || 0),
        0
      );
    } else if (otherFilter === POSITION_OTHER_FILTER.SERVICE_SAFETY) {
      availableStockValueTotal = content.reduce(
        (sum, row) =>
          sum +
          (Number(row.serviceSafetyStockValue ?? row.maintenanceReserveValue) ||
            0),
        0
      );
    } else if (otherFilter === POSITION_OTHER_FILTER.SALES_SAFETY) {
      availableStockValueTotal = content.reduce(
        (sum, row) => sum + (Number(row.salesSafetyStockValue) || 0),
        0
      );
    } else if (otherFilter === POSITION_OTHER_FILTER.RESERVED) {
      availableStockValueTotal = content.reduce(
        (sum, row) => sum + (Number(row.reservedStockValue) || 0),
        0
      );
    }

    return {
      ...page,
      content,
      systemSummaries: filteredSystemSummaries,
      availableStockValueTotal,
      totalElements:
        otherFilter && otherFilter !== "All"
          ? content.length
          : (page.totalElements ?? content.length),
      totalPages:
        otherFilter && otherFilter !== "All"
          ? content.length > 0
            ? 1
            : 0
          : (page.totalPages ?? (content.length > 0 ? 1 : 0)),
    };
  },

  /**
   * POST /saas/ims/challan/download — IMS11DD
   * Body must include exactly one of:
   *   { stockJournalId } — one journal / challan row
   *   { customerAllocationId } — fallback when a row has no stockJournalId
   * `ref` may be a journal id (legacy) or `{ stockJournalId }` / `{ customerAllocationId }`.
   * Row download should pass stockJournalId so each click yields one record.
   */
  downloadChallan: async (ref, { fallbackFilename } = {}) => {
    const source =
      ref != null && typeof ref === "object" && !Array.isArray(ref)
        ? ref
        : { stockJournalId: ref };

    const stockJournalId = toNumberOrNull(source.stockJournalId);
    const customerAllocationId = toNumberOrNull(source.customerAllocationId);
    const hasJournal = stockJournalId != null;
    const hasAllocation = customerAllocationId != null;
    if (hasJournal === hasAllocation) {
      throw new Error(
        "Pass exactly one of stockJournalId or customerAllocationId to download the challan PDF."
      );
    }

    const body = hasAllocation
      ? { customerAllocationId }
      : { stockJournalId };
    const fallbackId = hasAllocation ? customerAllocationId : stockJournalId;

    const { blob, contentDisposition } = await imsPostBlob(
      `/challan/download`,
      body,
      DELIVERY_CHALLAN_DOWNLOAD
    );

    if (!(blob instanceof Blob) || blob.size === 0) {
      throw new Error("Downloaded challan PDF is empty.");
    }

    const filename = parseContentDispositionFilename(
      contentDisposition,
      fallbackFilename ||
        (hasAllocation
          ? `delivery-challan-allocation-${fallbackId}.pdf`
          : `delivery-challan-journal-${fallbackId}.pdf`)
    );
    downloadBlob({
      blob,
      filename: /\.pdf$/i.test(filename) ? filename : `${filename}.pdf`,
    });

    return { filename };
  },

  /** GET /saas/ims/challan/search — IMS11S */
  getDeliveryChallans: async (filters = {}) => {
    const params = new URLSearchParams();
    params.set("page", String(filters.page ?? 0));
    params.set("size", String(filters.size ?? 10));

    const response = await imsGet(
      `/challan/search?${params.toString()}`,
      DELIVERY_CHALLAN_SEARCH
    );
    const page = unwrapPage(response, filters);
    const content = (page.content || []).map(mapDeliveryChallanRow).filter(Boolean);
    return {
      ...page,
      content,
      totalElements: page.totalElements ?? content.length,
      totalPages: page.totalPages ?? (content.length > 0 ? 1 : 0),
    };
  },

  /** PUT /saas/ims/challan/update/{deliveryChallanId} — IMS11U */
  updateDeliveryChallan: async (deliveryChallanId, payload) => {
    if (deliveryChallanId == null || String(deliveryChallanId).trim() === "") {
      throw new Error("Delivery challan ID is required");
    }
    const body = mapDeliveryChallanUpdatePayload(payload);
    const response = await imsPut(
      `/challan/update/${encodeURIComponent(deliveryChallanId)}`,
      body,
      DELIVERY_CHALLAN_UPDATE
    );
    return unwrapData(response) ?? { success: true };
  },

  /** GET /saas/ims/stock-journal/search — IMS05S */
  getStockJournal: async (filters = {}) => {
    const params = new URLSearchParams();
    params.set("page", String(filters.page ?? 0));
    params.set("size", String(filters.size ?? 10));

    const productId = toNumberOrNull(
      filters.productId && filters.productId !== "All" ? filters.productId : null
    );
    if (productId != null) params.set("productId", String(productId));

    const warehouseLocationId = toNumberOrNull(
      filters.warehouseLocationId && filters.warehouseLocationId !== "All"
        ? filters.warehouseLocationId
        : filters.warehouse && filters.warehouse !== "All"
          ? filters.warehouse
          : null
    );
    if (warehouseLocationId != null) {
      params.set("warehouseLocationId", String(warehouseLocationId));
    }

    const movementType = toApiMovementType(filters.movementType);
    if (movementType) {
      params.set("movementType", movementType);
    }

    if (filters.startDate || filters.fromDate) {
      params.set("fromDate", String(filters.startDate || filters.fromDate));
    }
    if (filters.endDate || filters.toDate) {
      params.set("toDate", String(filters.endDate || filters.toDate));
    }

    const response = await imsGet(
      `/stock-journal/search?${params.toString()}`,
      STOCK_JOURNAL_SEARCH
    );
    const page = unwrapPage(response, filters);
    const content = (page.content || []).map(mapStockJournalRow).filter(Boolean);
    return {
      ...page,
      content,
      totalElements: page.totalElements ?? content.length,
      totalPages: page.totalPages ?? (content.length > 0 ? 1 : 0),
    };
  },

  /** GET /saas/ims/product-catalog/search — IMS01S (paged for inventory dropdowns) */
  getInventoryProducts: async ({ page = 0, size = ALLOCATION_LOOKUP_SIZE } = {}) => {
    const result = await searchProducts({
      page,
      size,
      sortBy: "productName",
      sortDir: "ASC",
    });
    const content = (result.products || result.content || []).map((p) => ({
      id: String(p.id),
      name: p.name || displayOrId(null, p.id),
      partNumber: p.partNumber || "",
      category: p.category || "",
      status: p.status || "Active",
      underWarranty: Boolean(p.underWarranty),
    }));
    const totalPages = Number(result.totalPages ?? 0);
    const pageNumber = Number(result.pageNumber ?? result.number ?? page);
    const last =
      result.last != null
        ? Boolean(result.last)
        : totalPages > 0
          ? pageNumber >= totalPages - 1
          : content.length < size;
    return {
      content,
      page: pageNumber,
      size: Number(result.pageSize ?? result.size ?? size),
      totalElements: Number(result.totalElements ?? content.length),
      totalPages,
      last,
      hasMore: !last,
    };
  },

  /**
   * GET /saas/ims/inventory-locations/search/status?status=Active — IMS03S
   * Active warehouses only (paged) for inventory-management dropdowns.
   */
  getWarehouses: async ({
    page = 0,
    size = ALLOCATION_LOOKUP_SIZE,
    sortBy = "warehouseName",
    sortDir = "ASC",
  } = {}) => {
    const result = await searchWarehousesByStatus("Active", {
      page,
      size,
      sortBy,
      sortDir,
    });
    const content = (result.warehouses || result.content || []).map((w) => ({
      id: String(w.id),
      name: w.name || displayOrId(null, w.id),
    }));
    const totalPages = Number(result.totalPages ?? 0);
    const pageNumber = Number(result.pageNumber ?? result.number ?? page);
    const last =
      result.last != null
        ? Boolean(result.last)
        : totalPages > 0
          ? pageNumber >= totalPages - 1
          : content.length < size;
    return {
      content,
      page: pageNumber,
      size: Number(result.pageSize ?? result.size ?? size),
      totalElements: Number(result.totalElements ?? content.length),
      totalPages,
      last,
      hasMore: !last,
    };
  },

  /** GET /saas/ims/product-vendors/search — IMS02S */
  getVendors: async ({ page = 0, size = 10 } = {}) => {
    const result = await searchVendors({
      page,
      size,
      sortBy: "vendorId",
      sortDir: "ASC",
    });
    return (result.vendors || result.content || []).map((v) => ({
      id: String(v.id),
      name: v.tradeName || v.legalCompanyName || v.name || displayOrId(null, v.id),
    }));
  },

  /** GET /saas/crm/customer-info/search — CRM01S */
  getCustomers: async () => {
    const result = await searchCustomers({
      page: 0,
      size: 10,
      sortBy: "customerId",
      sortDir: "ASC",
    });
    return (result.items || []).map((c) => ({
      id: String(c.id),
      name: c.name || displayOrId(null, c.id),
    }));
  },

  /** POST /saas/ims/inventory-record/add — IMS05A */
  saveInventoryReceipt: async (payload) => {
    const body = mapInventoryReceiptPayload(payload);
    const response = await imsPost("/inventory-record/add", body, INVENTORY_RECORD_ADD);
    return unwrapData(response) ?? { success: true };
  },

  /** GET /saas/ims/inventory-record/search — IMS05S */
  getInventoryRecords: async (filters = {}) => {
    const params = new URLSearchParams();
    params.set("page", String(filters.page ?? 0));
    params.set("size", String(filters.size ?? 10));
    params.set("sortBy", String(filters.sortBy || "inventoryInId"));
    params.set("sortDir", String(filters.sortDir || "DESC"));

    const response = await imsGet(
      `/inventory-record/search?${params.toString()}`,
      INVENTORY_RECORD_SEARCH
    );
    const page = unwrapPage(response, filters);
    // productName / warehouseName / vendorName come from the search payload
    const content = (page.content || []).map(mapInventoryRecordListRow).filter(Boolean);
    return {
      ...page,
      content,
      totalElements: page.totalElements ?? content.length,
      totalPages: page.totalPages ?? (content.length > 0 ? 1 : 0),
    };
  },

  /**
   * Filtered inventory-record searches — IMS05S.
   * Paths: /search/batch | /search/product | /search/warehouse-location | /search/source-type
   */
  searchInventoryRecords: async (filters = {}) => {
    const pageNum = filters.page ?? 0;
    const size = filters.size ?? 10;
    const sortDir = String(filters.sortDir || "DESC");
    const params = new URLSearchParams();
    params.set("page", String(pageNum));
    params.set("size", String(size));
    params.set("sortDir", sortDir);

    const batchNumber = String(filters.batchNumber || "").trim();
    const productId = toNumberOrNull(
      filters.productId && filters.productId !== "All" ? filters.productId : null
    );
    const warehouseLocationId = toNumberOrNull(
      filters.warehouseLocationId && filters.warehouseLocationId !== "All"
        ? filters.warehouseLocationId
        : null
    );
    const sourceTypeRaw =
      filters.sourceType && filters.sourceType !== "All"
        ? toApiSourceType(filters.sourceType)
        : null;

    let path = "/inventory-record/search";
    let sortBy = String(filters.sortBy || "inventoryInId");

    if (batchNumber) {
      path = "/inventory-record/search/batch";
      params.set("batchNumber", batchNumber);
      sortBy = String(filters.sortBy || "receiptDate");
    } else if (productId != null) {
      path = "/inventory-record/search/product";
      params.set("productId", String(productId));
      sortBy = String(filters.sortBy || "receiptDate");
    } else if (warehouseLocationId != null) {
      path = "/inventory-record/search/warehouse-location";
      params.set("warehouseLocationId", String(warehouseLocationId));
      sortBy = String(filters.sortBy || "receiptDate");
    } else if (sourceTypeRaw) {
      path = "/inventory-record/search/source-type";
      params.set("sourceType", sourceTypeRaw);
      sortBy = String(filters.sortBy || "receiptDate");
    }

    params.set("sortBy", sortBy);

    const response = await imsGet(
      `${path}?${params.toString()}`,
      INVENTORY_RECORD_SEARCH
    );
    const page = unwrapPage(response, { page: pageNum, size, paginate: false });
    const content = (page.content || []).map(mapInventoryRecordListRow).filter(Boolean);
    return {
      ...page,
      content,
      totalElements: page.totalElements ?? content.length,
      totalPages: page.totalPages ?? (content.length > 0 ? 1 : 0),
    };
  },

  /** Resolve customer display names for inventory-record list rows (CRM). */
  enrichInventoryRecordCustomerNames,

  /** GET /saas/ims/inventory-record/{inventoryInId} — IMS05S */
  getInventoryRecordById: async (inventoryInId) => {
    if (inventoryInId == null || String(inventoryInId).trim() === "") {
      throw new Error("Inventory record ID is required");
    }
    const response = await imsGet(
      `/inventory-record/${encodeURIComponent(inventoryInId)}`,
      INVENTORY_RECORD_SEARCH
    );
    const mapped = mapInventoryRecordDetail(unwrapItem(response));
    if (!mapped?.inventoryInId) {
      throw new Error("Inventory record not found");
    }
    return mapped;
  },

  /** GET /saas/ims/inventory-record/{inventoryInId}/inventory-images — IMS05S */
  getInventoryRecordImages: async (inventoryInId) => {
    if (inventoryInId == null || String(inventoryInId).trim() === "") {
      throw new Error("Inventory record ID is required");
    }
    const response = await imsGet(
      `/inventory-record/${encodeURIComponent(inventoryInId)}/inventory-images`,
      INVENTORY_RECORD_SEARCH
    );
    return normalizeApiImageList(unwrapData(response));
  },

  /** GET /saas/ims/inventory-record/{inventoryInId}/defective-images — IMS05S */
  getInventoryRecordDefectiveImages: async (inventoryInId) => {
    if (inventoryInId == null || String(inventoryInId).trim() === "") {
      throw new Error("Inventory record ID is required");
    }
    const response = await imsGet(
      `/inventory-record/${encodeURIComponent(inventoryInId)}/defective-images`,
      INVENTORY_RECORD_SEARCH
    );
    return normalizeApiImageList(unwrapData(response));
  },

  /** PUT /saas/ims/inventory-record/update/{inventoryInId} — IMS05U */
  updateInventoryReceipt: async (inventoryInId, payload) => {
    if (inventoryInId == null || String(inventoryInId).trim() === "") {
      throw new Error("Inventory record ID is required");
    }
    const body = mapInventoryReceiptUpdatePayload(payload);
    const response = await imsPut(
      `/inventory-record/update/${encodeURIComponent(inventoryInId)}`,
      body,
      INVENTORY_RECORD_UPDATE
    );
    return unwrapData(response) ?? { success: true };
  },

  /** GET /saas/ims/product-catalog/search — IMS01S (paged for allocation dropdowns) */
  getAllocationProducts: async ({ page = 0, size = ALLOCATION_LOOKUP_SIZE } = {}) => {
    const [result, metricMap] = await Promise.all([
      searchProducts({
        page,
        size,
        sortBy: "productName",
        sortDir: "ASC",
      }),
      getMetricNameByIdMap(),
    ]);
    const content = (result.products || result.content || []).map((p) => {
      const metricId =
        p.metricId != null && p.metricId !== "" ? String(p.metricId) : "";
      const uom = resolveMetricDisplayName(
        metricId,
        firstDefined(p.metricName, p.unitOfMeasure, p.uom),
        metricMap
      );
      return {
        id: String(p.id ?? p.productId ?? ""),
        name: p.name || displayOrId(null, p.id),
        partNumber: p.partNumber || "",
        metricId,
        uom,
        manufacturer: p.manufacturer || "",
      };
    });
    const totalPages = Number(result.totalPages ?? 0);
    const pageNumber = Number(result.pageNumber ?? result.number ?? page);
    const last =
      result.last != null
        ? Boolean(result.last)
        : totalPages > 0
          ? pageNumber >= totalPages - 1
          : content.length < size;
    return {
      content,
      page: pageNumber,
      size: Number(result.pageSize ?? result.size ?? size),
      totalElements: Number(result.totalElements ?? content.length),
      totalPages,
      last,
      hasMore: !last,
    };
  },

  /** GET /saas/crm/customer-info/search — CRM01S (paged for allocation dropdowns) */
  getAllocationCustomers: async ({ page = 0, size = ALLOCATION_LOOKUP_SIZE } = {}) => {
    const result = await searchCustomers({
      page,
      size,
      sortBy: "customerId",
      sortDir: "ASC",
    });
    const content = (result.items || []).map((c) => ({
      id: String(c.id),
      name: c.name || displayOrId(null, c.id),
    }));
    return {
      content,
      page: Number(result.page ?? page),
      size: Number(result.size ?? size),
      totalElements: Number(result.total ?? content.length),
      totalPages: Number(result.totalPages ?? 0),
      last: !Boolean(result.hasMore),
      hasMore: Boolean(result.hasMore),
    };
  },

  /**
   * Available layers for a product from stock-position detail (search as fallback).
   * GET /saas/ims/stock-position/{productId}/detail?customerId={customerId} — IMS05S
   * GET /saas/ims/stock-position/search — IMS05S (fallback layers only)
   *
   * Line-level availableQty is summed from detail warehouses'
   * salesAvailable or serviceAvailable based on allocationType.
   *
   * @param {string|number} productId
   * @param {string} [allocationType] Sales | Service — picks salesAvailable vs serviceAvailable
   * @param {string|number} [customerId] Header-level customer — required for customer allocation
   */
  getAllocationBatches: async (
    productId,
    allocationType = "Sales",
    customerId
  ) => {
    const prodId = toNumberOrNull(productId);
    if (prodId == null) {
      return { availableQty: 0, batches: [] };
    }

    const isSales = String(allocationType || "Sales").trim().toLowerCase() !== "service";
    const pickAvailable = (row) => {
      if (!row) return 0;
      if (isSales) {
        return Number(
          row.salesAvailable ?? row.availableStock ?? row.availableQty
        ) || 0;
      }
      return Number(
        row.serviceAvailable ?? row.availableStock ?? row.availableQty
      ) || 0;
    };

    let availableQty = 0;
    let batches = [];
    let detailOk = false;

    try {
      const detail = await inventoryManagementService.getStockPositionDetail(
        prodId,
        allocationType,
        customerId
      );
      detailOk = true;
      // Line-level available comes from detail warehouses (not search).
      availableQty = (detail.warehouses || []).reduce(
        (sum, wh) => sum + pickAvailable(wh),
        0
      );
      for (const wh of detail.warehouses || []) {
        const whBatches = Array.isArray(wh.batches) ? wh.batches : [];
        if (whBatches.length > 0) {
          // Batch rows expose free qty; for Sales, cap by warehouse salesAvailable FIFO.
          const warehouseCap = pickAvailable(wh);
          const sorted = [...whBatches].sort((a, b) => {
            const da = a.receiptDate || "";
            const db = b.receiptDate || "";
            if (da !== db) return String(da).localeCompare(String(db));
            return String(a.batchNumber || "").localeCompare(String(b.batchNumber || ""));
          });
          let remainingCap = warehouseCap;
          for (const b of sorted) {
            const freeQty =
              Number(b.availableStock ?? b.availableQty ?? b.serviceAvailable) || 0;
            if (freeQty <= 0) continue;
            const typedQty = isSales
              ? Math.min(freeQty, Math.max(0, remainingCap))
              : Number(b.serviceAvailable ?? freeQty) || 0;
            if (isSales) remainingCap = Math.max(0, remainingCap - typedQty);
            if (typedQty <= 0) continue;
            batches.push({
              key: `${wh.warehouseId}::${b.batchNumber}::${b.receiptDate || ""}::${b.purchasePricePerUnit || 0}`,
              productId: String(prodId),
              warehouseId: String(wh.warehouseId ?? ""),
              warehouse: wh.warehouseName || displayOrId(null, wh.warehouseId),
              batchNumber: b.batchNumber || "—",
              availableQty: typedQty,
              purchasePricePerUnit: Number(b.purchasePricePerUnit) || 0,
              receiptDate: b.receiptDate || null,
              warrantyEndDate: b.warrantyEnd || null,
            });
          }
        } else if (pickAvailable(wh) > 0) {
          const qty = pickAvailable(wh);
          batches.push({
            key: `${wh.warehouseId}::layer::${qty}`,
            productId: String(prodId),
            warehouseId: String(wh.warehouseId ?? ""),
            warehouse: wh.warehouseName || displayOrId(null, wh.warehouseId),
            batchNumber: "—",
            availableQty: qty,
            purchasePricePerUnit: Number(detail.product?.listPrice) || 0,
            receiptDate: null,
            warrantyEndDate: null,
          });
        }
      }
    } catch (err) {
      console.warn("Stock position detail unavailable; using search rows as layers:", err);
    }

    if (!detailOk || batches.length === 0) {
      const positionsPage = await inventoryManagementService.getStockPositions({
        productId: prodId,
        page: 0,
        size: 10,
        sortBy: "productName",
        sortDir: "ASC",
      });
      const positions = (positionsPage.content || []).filter(
        (row) => String(row.productId) === String(prodId)
      );
      if (!detailOk) {
        availableQty = positions.reduce((sum, row) => sum + pickAvailable(row), 0);
      }
      if (batches.length === 0) {
        batches = positions
          .filter((row) => pickAvailable(row) > 0)
          .map((row) => {
            const qty = pickAvailable(row);
            return {
              key: `${row.warehouseLocationId}::pos::${row.id || qty}`,
              productId: String(prodId),
              warehouseId: String(row.warehouseLocationId ?? ""),
              warehouse: row.warehouse || displayOrId(null, row.warehouseLocationId),
              batchNumber: "—",
              availableQty: qty,
              purchasePricePerUnit: Number(row.listPrice) || 0,
              receiptDate: null,
              warrantyEndDate: null,
            };
          });
      }
    }

    return { availableQty, batches };
  },

  getBatchRecommendations: async (
    productId,
    allocationType = "Sales",
    customerId
  ) => {
    const result = await inventoryManagementService.getAllocationBatches(
      productId,
      allocationType,
      customerId
    );
    return result.batches || [];
  },

  /** POST /saas/ims/customer-allocation — IMS08A */
  createAllocation: async (payload = {}) => {
    const allocationType =
      emptyToNull(payload.allocationType ?? payload.orderType) || "Sales";
    const body = {
      allocationType,
      orderType: allocationType,
      orderDate: emptyToNull(payload.orderDate ?? payload.date),
      iofNumber: emptyToNull(payload.iofNumber),
      customerId: toNumberOrNull(payload.customerId),
      projectName: emptyToNull(payload.projectName),
      customerInvoiceNumber: emptyToNull(
        payload.customerInvoiceNumber ?? payload.invoiceNumber
      ),
      customerInvoiceDate: emptyToNull(
        payload.customerInvoiceDate ?? payload.invoiceDate
      ),
      deliveryDate: emptyToNull(payload.deliveryDate),
      dispatchMethod: emptyToNull(payload.dispatchMethod) || "FIFO",
      lines: (payload.lines || payload.allocationLines || []).map((line) => {
        const overrides = (line.priceOverrides || line.batchesUsed || [])
          .map((b) => ({
            warehouseId: toNumberOrNull(b.warehouseId),
            batchNumber: emptyToNull(b.batchNumber) || "",
            purchasePricePerUnit:
              Number(b.purchasePricePerUnit ?? b.purchasePrice) || 0,
            allocatedQuantity:
              Number(b.allocatedQuantity ?? b.qty ?? b.quantity) || 0,
            overrideReason: emptyToNull(b.overrideReason) || "test",
          }))
          .filter((b) => (Number(b.allocatedQuantity) || 0) > 0);

        return {
          productId: toNumberOrNull(line.productId),
          iofQuantity:
            Number(line.iofQuantity ?? line.iofQty ?? line.requestedQty) || 0,
          consideredCostPerUnit:
            Number(line.consideredCostPerUnit ?? line.consideredCost) || 0,
          priceOverrides: overrides,
        };
      }),
    };

    const response = await imsPost("/customer-allocation", body, CUSTOMER_ALLOCATION_ADD);
    const data = unwrapData(response) ?? {};
    return {
      ...data,
      success: true,
      allocationId: firstDefined(
        data.customerAllocationId,
        data.allocationId,
        data.id
      ),
      // Raw lines from create — used to decide Blocked_by_price vs Eligible before unlock.
      lines: Array.isArray(data.lines) ? data.lines : [],
    };
  },

  /** GET /saas/ims/customer-allocation/search — IMS08S */
  getCustomerAllocations: async (filters = {}) => {
    const params = new URLSearchParams();
    params.set("page", String(filters.page ?? 0));
    params.set("size", String(filters.size ?? 10));

    if (filters.sortBy) params.set("sortBy", String(filters.sortBy));
    if (filters.sortDir) params.set("sortDir", String(filters.sortDir));

    const customerId = toNumberOrNull(
      filters.customerId && filters.customerId !== "All" ? filters.customerId : null
    );
    if (customerId != null) params.set("customerId", String(customerId));

    if (filters.status && filters.status !== "All") {
      params.set("status", String(filters.status));
    }

    const response = await imsGet(
      `/customer-allocation/search?${params.toString()}`,
      CUSTOMER_ALLOCATION_SEARCH
    );
    const page = unwrapPage(response, filters);
    const content = (page.content || []).map(mapCustomerAllocationListRow).filter(Boolean);
    return {
      ...page,
      content,
      totalElements: page.totalElements ?? content.length,
      totalPages: page.totalPages ?? (content.length > 0 ? 1 : 0),
    };
  },

  /** GET /saas/ims/customer-allocation/{customerAllocationId} — IMS08S */
  getAllocationDetail: async (id) => {
    if (id == null || String(id).trim() === "") {
      throw new Error("Allocation ID is required");
    }
    const [response, metricMap] = await Promise.all([
      imsGet(`/customer-allocation/${encodeURIComponent(id)}`, CUSTOMER_ALLOCATION_SEARCH),
      getMetricNameByIdMap(),
    ]);
    const mapped = mapCustomerAllocationDetail(unwrapItem(response));
    if (!mapped?.header?.allocationId) {
      throw new Error("Allocation not found");
    }
    mapped.products = (mapped.products || []).map((product) => ({
      ...product,
      uom: resolveMetricDisplayName(product.metricId, product.uom, metricMap),
    }));
    return mapped;
  },

  /**
   * PUT /saas/ims/customer-allocation/{customerAllocationId} — IMS08U
   * Setting lines[].deliveryDate marks that line Delivered (Outward + release reservation).
   */
  updateCustomerAllocation: async (customerAllocationId, payload = {}) => {
    if (customerAllocationId == null || String(customerAllocationId).trim() === "") {
      throw new Error("Allocation ID is required");
    }

    const allocationType =
      emptyToNull(payload.allocationType ?? payload.orderType) || "Sales";
    const body = {
      allocationType,
      orderType: allocationType,
      orderDate: emptyToNull(toIsoDateString(payload.orderDate ?? payload.date)),
      iofNumber: emptyToNull(payload.iofNumber),
      customerId: toNumberOrNull(payload.customerId),
      projectName: emptyToNull(payload.projectName),
      customerInvoiceNumber: emptyToNull(
        payload.customerInvoiceNumber ?? payload.invoiceNumber
      ),
      customerInvoiceDate: emptyToNull(
        toIsoDateString(payload.customerInvoiceDate ?? payload.invoiceDate)
      ),
      deliveryDate: emptyToNull(toIsoDateString(payload.deliveryDate)),
      dispatchMethod: emptyToNull(payload.dispatchMethod) || "FIFO",
      lines: (payload.lines || []).map((line) => {
        const lineBody = {
          productId: toNumberOrNull(line.productId),
          iofQuantity:
            Number(line.iofQuantity ?? line.iofQty ?? line.requestedQty) || 0,
          consideredCostPerUnit:
            Number(line.consideredCostPerUnit ?? line.consideredCost) || 0,
        };

        const lineId = toNumberOrNull(
          line.customerAllocationLineId ?? line.lineId ?? line.id
        );
        if (lineId != null) {
          lineBody.customerAllocationLineId = lineId;
        }

        const lineDeliveryDate = toIsoDateString(line.deliveryDate);
        if (lineDeliveryDate) {
          lineBody.deliveryDate = lineDeliveryDate;
        }

        const lineInvoiceNumber = emptyToNull(
          line.customerInvoiceNumber ?? line.invoiceNumber
        );
        if (lineInvoiceNumber != null) {
          lineBody.customerInvoiceNumber = lineInvoiceNumber;
        }

        const lineDeliveryNotes = emptyToNull(
          line.deliveryNotes ?? line.notes ?? line.remarks
        );
        if (lineDeliveryNotes != null) {
          lineBody.deliveryNotes = lineDeliveryNotes;
        }

        const lineChallanToAddress = emptyToNull(line.challanToAddress);
        if (lineChallanToAddress != null) {
          lineBody.challanToAddress = lineChallanToAddress;
        }

        const lineChallanToMobile = emptyToNull(line.challanToMobile);
        if (lineChallanToMobile != null) {
          lineBody.challanToMobile = lineChallanToMobile;
        }

        const overrides = (line.priceOverrides || line.batchesUsed || [])
          .map((b) => ({
            warehouseId: toNumberOrNull(b.warehouseId),
            batchNumber: emptyToNull(b.batchNumber) || "",
            purchasePricePerUnit:
              Number(b.purchasePricePerUnit ?? b.purchasePrice) || 0,
            allocatedQuantity:
              Number(b.allocatedQuantity ?? b.qty ?? b.quantity ?? b.allocatedQty) || 0,
            overrideReason:
              emptyToNull(b.overrideReason) || "Existing allocation detail",
          }))
          .filter((b) => (Number(b.allocatedQuantity) || 0) > 0 && b.warehouseId != null);

        if (overrides.length > 0) {
          lineBody.priceOverrides = overrides;
        }

        return lineBody;
      }),
    };

    const response = await imsPut(
      `/customer-allocation/${encodeURIComponent(customerAllocationId)}`,
      body,
      CUSTOMER_ALLOCATION_UPDATE
    );
    const data = unwrapData(response) ?? {};
    return {
      ...data,
      success: true,
      allocationId: firstDefined(
        data.customerAllocationId,
        data.allocationId,
        data.id,
        customerAllocationId
      ),
      lines: Array.isArray(data.lines) ? data.lines : [],
    };
  },

  /**
   * Mark one or more allocation lines as delivered via the update endpoint.
   * `deliveries` is `[{ lineId, deliveryDate, invoiceNumber?, deliveryNotes?, challanToAddress?, challanToMobile? }, ...]`.
   * Other lines keep existing values; already-delivered lines keep their dates.
   */
  markAllocationLinesDelivered: async ({ allocation, deliveries = [] } = {}) => {
    const header = allocation?.header;
    const products = allocation?.products || [];
    if (!header?.allocationId) {
      throw new Error("Allocation is required");
    }
    if (!Array.isArray(deliveries) || deliveries.length === 0) {
      throw new Error("At least one allocation line is required");
    }

    const deliveryMap = new Map();
    for (const item of deliveries) {
      const lineId = String(item?.lineId ?? "");
      const deliveryDateIso = toIsoDateString(item?.deliveryDate);
      if (!lineId || !deliveryDateIso) {
        throw new Error("Each delivery requires a line id and delivery date");
      }
      deliveryMap.set(lineId, {
        deliveryDate: deliveryDateIso,
        customerInvoiceNumber: emptyToNull(
          item?.customerInvoiceNumber ?? item?.invoiceNumber
        ),
        deliveryNotes: emptyToNull(item?.deliveryNotes ?? item?.notes),
        challanToAddress: emptyToNull(item?.challanToAddress),
        challanToMobile: emptyToNull(item?.challanToMobile),
      });
    }

    const lines = products.map((product) => {
      const id = String(product.customerAllocationLineId || product.id);
      const linePayload = {
        customerAllocationLineId: product.customerAllocationLineId || product.id,
        productId: product.productId,
        iofQuantity: product.iofQuantity ?? product.requestedQty,
        consideredCostPerUnit: product.consideredCostPerUnit,
      };

      if (deliveryMap.has(id)) {
        const delivery = deliveryMap.get(id);
        linePayload.deliveryDate = delivery.deliveryDate;
        if (delivery.customerInvoiceNumber != null) {
          linePayload.customerInvoiceNumber = delivery.customerInvoiceNumber;
        }
        if (delivery.deliveryNotes != null) {
          linePayload.deliveryNotes = delivery.deliveryNotes;
        }
        if (delivery.challanToAddress != null) {
          linePayload.challanToAddress = delivery.challanToAddress;
        }
        if (delivery.challanToMobile != null) {
          linePayload.challanToMobile = delivery.challanToMobile;
        }
      } else if (product.deliveryDate) {
        linePayload.deliveryDate = toIsoDateString(product.deliveryDate);
        if (product.invoiceNumber && product.invoiceNumber !== "—") {
          linePayload.customerInvoiceNumber = product.invoiceNumber;
        }
        if (product.deliveryNotes) {
          linePayload.deliveryNotes = product.deliveryNotes;
        }
        if (product.challanToAddress) {
          linePayload.challanToAddress = product.challanToAddress;
        }
        if (product.challanToMobile) {
          linePayload.challanToMobile = product.challanToMobile;
        }
      }

      return linePayload;
    });

    const allocationType =
      header.allocationType || header.orderType || "Sales";
    return inventoryManagementService.updateCustomerAllocation(header.allocationId, {
      allocationType,
      orderType: allocationType,
      orderDate: header.date,
      iofNumber: header.iofNumber === "—" ? null : header.iofNumber,
      customerId: header.customerId,
      projectName: header.projectName === "—" ? null : header.projectName,
      customerInvoiceNumber:
        header.invoiceNumber === "—" ? null : header.invoiceNumber,
      customerInvoiceDate: header.invoiceDate,
      deliveryDate: header.deliveryDate,
      dispatchMethod: header.dispatchMethod,
      lines,
    });
  },

  /**
   * Mark a single allocation line as delivered via the existing update endpoint.
   * Other lines keep existing field values; only the target line gets deliveryDate.
   */
  markAllocationLineDelivered: async ({
    allocation,
    lineId,
    deliveryDate,
  } = {}) => {
    return inventoryManagementService.markAllocationLinesDelivered({
      allocation,
      deliveries: [{ lineId, deliveryDate }],
    });
  },

  /**
   * POST /saas/ims/customer-allocation/revoke-unlock — IMS08A
   * Creates a revoke or unlock request for one allocation detail line.
   */
  createRevokeUnlockRequest: async (payload = {}) => {
    const customerAllocationDetailId = toNumberOrNull(
      firstDefined(
        payload.customerAllocationDetailId,
        payload.customerAllocationLineDetailId,
        payload.customer_allocation_line_detail_id
      )
    );
    if (customerAllocationDetailId == null) {
      throw new Error("customerAllocationDetailId is required");
    }

    const actionType = String(
      firstDefined(payload.actionType, payload.action_type, "REVOKE")
    )
      .trim()
      .toUpperCase();

    const body = {
      customerAllocationDetailId,
      actionType: actionType || "REVOKE",
      reason: emptyToNull(payload.reason) || "",
    };

    if (Array.isArray(payload.pendingWithEmployee)) {
      const pendingWithEmployee = payload.pendingWithEmployee
        .map((id) => toNumberOrNull(id))
        .filter((id) => id != null);
      if (pendingWithEmployee.length > 0) {
        body.pendingWithEmployee = pendingWithEmployee;
      }
    }

    const lineId = toNumberOrNull(
      firstDefined(
        payload.customerAllocationLineId,
        payload.customer_allocation_line_id
      )
    );
    if (lineId != null) {
      body.customerAllocationLineId = lineId;
    }

    if (actionType === "REVOKE" || payload.revokeQuantity != null) {
      body.revokeQuantity = toFiniteNumber(
        firstDefined(payload.revokeQuantity, payload.revoke_quantity),
        0
      );
    }

    if (actionType === "UNLOCK" || payload.unlockedPrice != null) {
      const unlockedPrice = toNumberOrNull(
        firstDefined(payload.unlockedPrice, payload.unlocked_price)
      );
      if (unlockedPrice != null) {
        body.unlockedPrice = unlockedPrice;
      }
    }

    const response = await imsPost(
      "/customer-allocation/revoke-unlock",
      body,
      CUSTOMER_ALLOCATION_ADD
    );
    return unwrapData(response) ?? { success: true };
  },

  /** @deprecated Prefer createRevokeUnlockRequest */
  submitRevokeRequest: async (payload = {}) => {
    return inventoryManagementService.createRevokeUnlockRequest(payload);
  },

  /**
   * GET /saas/ims/customer-allocation/revoke-unlock/pending — IMS12S
   */
  getPendingRevokeUnlockRequests: async () => {
    const response = await imsGet(
      "/customer-allocation/revoke-unlock/pending",
      REVOKE_UNLOCK_SEARCH
    );
    const data = unwrapData(response);
    let rows = [];
    if (Array.isArray(data)) {
      rows = data;
    } else if (data && typeof data === "object") {
      const page = unwrapPage(response);
      rows =
        page.content ||
        data.requests ||
        data.revokeUnlockRequests ||
        data.pendingRequests ||
        [];
      if (!Array.isArray(rows)) rows = [];
    }

    const content = rows
      .map((row) => {
        if (!row || typeof row !== "object") return null;
        const id = firstDefined(
          row.allocationRevokeUnlockId,
          row.allocation_revoke_unlock_id,
          row.id,
          row.revokeUnlockId,
          row.revoke_unlock_id,
          row.imsAllocationRevokeUnlockId,
          row.ims_allocation_revoke_unlock_id
        );
        if (id == null || id === "") return null;

        const actionTypeRaw = String(
          firstDefined(row.actionType, row.action_type, "") || ""
        ).toUpperCase();

        return {
          id: String(id),
          actionType: actionTypeRaw || "—",
          customerAllocationDetailId: firstDefined(
            row.customerAllocationDetailId,
            row.customer_allocation_detail_id
          ),
          customerAllocationLineId: firstDefined(
            row.customerAllocationLineId,
            row.customer_allocation_line_id
          ),
          customerAllocationId: firstDefined(
            row.customerAllocationId,
            row.customer_allocation_id,
            row.allocationId,
            row.allocation_id
          ),
          reference:
            firstDefined(
              row.reference,
              row.allocationReference,
              row.allocation_reference,
              row.customerAllocationReference,
              row.iofNumber,
              row.iof_number
            ) || "—",
          customerName:
            firstDefined(row.customerName, row.customer_name) || "—",
          productName:
            firstDefined(row.productName, row.product_name, row.name) || "—",
          partNumber: firstDefined(row.partNumber, row.part_number) || "—",
          batchNumber: firstDefined(row.batchNumber, row.batch_number) || "—",
          warehouseId: firstDefined(row.warehouseId, row.warehouse_id) ?? null,
          warehouseName:
            firstDefined(row.warehouseName, row.warehouse_name) || "—",
          purchasePricePerUnit: toFiniteNumber(
            firstDefined(
              row.purchasePricePerUnit,
              row.purchase_price_per_unit
            )
          ),
          allocatedQuantity: toFiniteNumber(
            firstDefined(
              row.allocatedQuantity,
              row.allocated_quantity,
              row.allocatedQty
            )
          ),
          unlockedPrice:
            firstDefined(row.unlockedPrice, row.unlocked_price) != null
              ? toFiniteNumber(
                  firstDefined(row.unlockedPrice, row.unlocked_price)
                )
              : null,
          revokeQuantity: toFiniteNumber(
            firstDefined(row.revokeQuantity, row.revoke_quantity)
          ),
          reason: firstDefined(row.reason, row.requestReason) || "—",
          status: String(
            firstDefined(row.status, row.requestStatus) || "PENDING"
          ).toUpperCase(),
          lineStatus: firstDefined(row.lineStatus, row.line_status) || "—",
          detailStatus: firstDefined(
            row.detailStatus,
            row.detail_status,
            row.lineDetailStatus
          ) || "—",
          iofNumber: firstDefined(row.iofNumber, row.iof_number) || "—",
          requestedAt:
            firstDefined(
              row.requestedAt,
              row.requested_at,
              row.createdAt,
              row.created_at
            ) || null,
          reviewedAt:
            firstDefined(row.reviewedAt, row.reviewed_at) || null,
          raw: row,
        };
      })
      .filter(Boolean);

    return content;
  },

  /**
   * PUT /saas/ims/customer-allocation/revoke-unlock/{id}/approve — IMS12U
   */
  approveRevokeUnlockRequest: async (id) => {
    if (id == null || id === "") {
      throw new Error("Request id is required");
    }
    const response = await imsPut(
      `/customer-allocation/revoke-unlock/${encodeURIComponent(id)}/approve`,
      {},
      REVOKE_UNLOCK_UPDATE
    );
    return unwrapData(response) ?? { success: true };
  },

  /**
   * PUT /saas/ims/customer-allocation/revoke-unlock/{id}/reject — IMS12U
   */
  rejectRevokeUnlockRequest: async (id) => {
    if (id == null || id === "") {
      throw new Error("Request id is required");
    }
    const response = await imsPut(
      `/customer-allocation/revoke-unlock/${encodeURIComponent(id)}/reject`,
      {},
      REVOKE_UNLOCK_UPDATE
    );
    return unwrapData(response) ?? { success: true };
  },

  /** Active warehouses for stock-transfer dropdowns — same IMS03S status search as getWarehouses. */
  getTransferWarehouses: async ({ page = 0, size = TRANSFER_PAGE_SIZE } = {}) => {
    return inventoryManagementService.getWarehouses({ page, size });
  },

  /**
   * GET /saas/crm/customer-locations/search/customer — CRM04S
   * Address options for stock-transfer customer party.
   */
  getTransferCustomerLocations: async ({
    customerId,
    page = 0,
    size = TRANSFER_PAGE_SIZE,
  } = {}) => {
    const id = toNumberOrNull(customerId);
    if (id == null) return [];

    const locations = await searchCustomerLocations({
      customerId: id,
      page,
      size,
      sortBy: "locationId",
      sortDir: "ASC",
    });

    return (locations || [])
      .map((loc) => {
        const raw = loc?.raw || loc || {};
        const address = formatPartyAddress({
          locationName: raw.locationName || loc?.label,
          addressLine1: raw.addressLine1 || raw.address_line1,
          addressLine2: raw.addressLine2 || raw.address_line2,
          city: raw.city,
          state: raw.state,
          pincode: raw.pincode || raw.pinCode || raw.postalCode,
          address: raw.address || raw.fullAddress,
        });
        const label = address || String(loc?.label || "").trim();
        const optionId = loc?.id || (loc?.locationId != null ? String(loc.locationId) : "");
        if (!optionId || !label) return null;
        return { id: String(optionId), label, address: label };
      })
      .filter(Boolean);
  },

  /**
   * GET /saas/crm/customer-contact/search/customer — CRM02S
   * Mobile options for stock-transfer customer party.
   */
  getTransferCustomerMobiles: async ({
    customerId,
    page = 0,
    size = TRANSFER_PAGE_SIZE,
  } = {}) => {
    const id = toNumberOrNull(customerId);
    if (id == null) return [];

    const contacts = await searchCustomerContacts({
      customerId: id,
      page,
      size,
      sortBy: "contactId",
      sortDir: "ASC",
    });

    const seen = new Set();
    const options = [];
    for (const contact of contacts || []) {
      const raw = contact?.raw || contact || {};
      const mobile = String(
        firstDefined(
          contact?.phone,
          raw.mobileNumber,
          raw.mobile,
          raw.phone,
          raw.primaryMobile,
          raw.primary_mobile
        ) || ""
      ).trim();
      if (!mobile || seen.has(mobile)) continue;
      seen.add(mobile);
      const optionId =
        contact?.id ||
        (contact?.contactId != null ? String(contact.contactId) : mobile);
      options.push({
        id: String(optionId),
        label: mobile,
        mobile,
      });
    }
    return options;
  },

  /**
   * GET /saas/ims/product-vendors/{vendorId} — IMS02S
   * Address + mobile options for stock-transfer vendor party.
   */
  getTransferVendorPartyDetails: async (vendorId) => {
    const id = toNumberOrNull(vendorId);
    if (id == null) {
      return { addresses: [], mobiles: [] };
    }

    const vendor = await fetchVendorById(id);
    if (!vendor) return { addresses: [], mobiles: [] };

    const addressLabel = formatPartyAddress({
      addressLine1: vendor.addressLine1,
      addressLine2: vendor.addressLine2,
      city: vendor.addressCity,
      state: vendor.addressState,
      pincode: vendor.addressPincode,
      address: vendor.address,
    });

    const addresses = addressLabel
      ? [{ id: "registered", label: addressLabel, address: addressLabel }]
      : [];

    const mobiles = [];
    const seen = new Set();
    for (const [key, value] of [
      ["primary", vendor.phone],
      ["secondary", vendor.secondaryMobile],
      ["alternate", vendor.alternatePhone],
    ]) {
      const mobile = String(value || "").trim();
      if (!mobile || seen.has(mobile)) continue;
      seen.add(mobile);
      mobiles.push({ id: key, label: mobile, mobile });
    }

    return { addresses, mobiles };
  },

  /** GET /saas/ims/product-vendors/search/status — IMS02S (active vendors for stock transfer) */
  getTransferVendors: async ({ page = 0, size = TRANSFER_PAGE_SIZE } = {}) => {
    const result = await searchVendorsByStatus("Active", {
      page,
      size,
      sortBy: "legalCompanyName",
      sortDir: "ASC",
    });
    const list = result.vendors || result.content || [];
    const content = list.map((v) => ({
      id: String(v.id ?? v.vendorId),
      name:
        v.tradeName ||
        v.legalCompanyName ||
        v.name ||
        displayOrId(null, v.id ?? v.vendorId),
    }));
    const totalPages = Number(result.totalPages ?? 0);
    const pageNumber = Number(result.pageNumber ?? result.number ?? page);
    const last =
      result.last != null
        ? Boolean(result.last)
        : totalPages > 0
          ? pageNumber >= totalPages - 1
          : content.length < size;
    return {
      content,
      page: pageNumber,
      size: Number(result.pageSize ?? result.size ?? size),
      totalElements: Number(result.totalElements ?? content.length),
      totalPages,
      last,
      hasMore: !last,
    };
  },

  /**
   * Inventory lines at a warehouse for the stock-transfer product and batch dropdowns.
   * GET /saas/ims/inventory-record/search/warehouse-location — IMS05S
   */
  getTransferProducts: async ({
    warehouseLocationId,
    page = 0,
    size = TRANSFER_PAGE_SIZE,
  } = {}) => {
    const warehouseId = toNumberOrNull(warehouseLocationId);
    if (warehouseId == null) {
      return {
        content: [],
        page: 0,
        size,
        totalElements: 0,
        totalPages: 0,
        last: true,
        hasMore: false,
      };
    }

    const params = new URLSearchParams();
    params.set("warehouseLocationId", String(warehouseId));
    params.set("page", String(page));
    params.set("size", String(size));
    params.set("sortBy", "receiptDate");
    params.set("sortDir", "DESC");

    const response = await imsGet(
      `/inventory-record/search/warehouse-location?${params.toString()}`,
      INVENTORY_RECORD_SEARCH
    );
    const pageResult = unwrapPage(response, { page, size, paginate: false });
    const content = (pageResult.content || [])
      .map((row, index) => mapWarehouseLocationInventoryRow(row, page, index))
      .filter(Boolean);

    const totalPages = Number(pageResult.totalPages ?? 0);
    const pageNumber = Number(pageResult.pageNumber ?? pageResult.number ?? page);
    const last =
      pageResult.last != null
        ? Boolean(pageResult.last)
        : totalPages > 0
          ? pageNumber >= totalPages - 1
          : content.length < size;

    return {
      content,
      page: pageNumber,
      size: Number(pageResult.pageSize ?? pageResult.size ?? size),
      totalElements: Number(pageResult.totalElements ?? content.length),
      totalPages,
      last,
      hasMore: !last,
    };
  },

  /**
   * On-hand quantities for one batch. The transfer form displays these values as returned.
   * GET /saas/ims/stock-transfer/batch-stock — IMS05S
   */
  getTransferBatchStock: async ({
    productId,
    warehouseLocationId,
    batchNumber,
  } = {}) => {
    const prodId = toNumberOrNull(productId);
    const warehouseId = toNumberOrNull(warehouseLocationId);
    const batch = String(batchNumber || "").trim();
    if (prodId == null || warehouseId == null || !batch) return null;

    const params = new URLSearchParams();
    params.set("productId", String(prodId));
    params.set("warehouseLocationId", String(warehouseId));
    params.set("batchNumber", batch);

    const response = await imsGet(
      `/stock-transfer/batch-stock?${params.toString()}`,
      INVENTORY_RECORD_SEARCH
    );
    const data = unwrapItem(response);
    if (!data || typeof data !== "object") return null;

    return {
      productId: firstDefined(data.productId, data.product_id) ?? prodId,
      warehouseLocationId:
        firstDefined(data.warehouseLocationId, data.warehouse_location_id) ??
        warehouseId,
      batchNumber: firstDefined(data.batchNumber, data.batch_number) ?? batch,
      availableQuantity:
        firstDefined(data.availableQuantity, data.available_quantity) ?? null,
      defectiveQuantity:
        firstDefined(data.defectiveQuantity, data.defective_quantity) ?? null,
    };
  },

  /**
   * Legacy journal aggregation. The stock-transfer form does not use this.
   * Available and defective quantities come from getTransferBatchStock.
   *
   * Batches for a product at source warehouse, aggregated from journal transferQuantity.
   * Loads all journal pages for the product so available qty is complete.
   *
   * defectiveQty = Σ |Defect Adjustment|
   *                − Σ |Defective Replacement Request to Vendor|
   *                − Σ |OEM Warranty Replacement Request to Vendor| (clamped ≥ 0).
   *
   * availableQty sums signed transferQuantity for every movement type returned by the
   * journal (no type whitelist). Transfer In / Out are filtered by warehouse party name
   * (In → toName, Out → fromName).
   */
  getTransferBatches: async ({
    warehouseLocationId,
    productId,
    warehouseName: warehouseNameHint,
    includeZeroAvailable = false,
  } = {}) => {
    const warehouseId = toNumberOrNull(warehouseLocationId);
    const prodId = toNumberOrNull(productId);
    if (warehouseId == null || prodId == null) {
      return { content: [] };
    }

    let warehouseName = String(warehouseNameHint || "").trim();
    if (!warehouseName) {
      try {
        const wh = await fetchWarehouseById(warehouseId);
        warehouseName = String(wh?.name || wh?.warehouseName || "").trim();
      } catch (_) {
        /* name matching falls back to including all rows */
      }
    }

    const byBatch = new Map();
    let page = 0;
    let hasMore = true;

    while (hasMore) {
      const pageResult = await inventoryManagementService.getStockJournal({
        warehouseLocationId: warehouseId,
        productId: prodId,
        page,
        size: TRANSFER_PAGE_SIZE,
      });
      const journalRows = pageResult.content || [];

      for (const row of journalRows) {
        const batchNumber =
          row.batchNumber && row.batchNumber !== "—" ? String(row.batchNumber) : "";
        if (!batchNumber) continue;

        const qty = journalQtyForWarehouse(row, warehouseName);
        const movementType = String(row?.movementType || "")
          .replace(/_/g, " ")
          .trim()
          .toLowerCase();
        const isDefectAdjustment = movementType === "defect adjustment";
        const isRtvDefectiveReplacement =
          movementType === "defective replacement request to vendor" ||
          movementType === "return to vendor defective replacement";
        const isRtvWarrantyReplacement =
          movementType === "oem warranty replacement request to vendor" ||
          movementType === "return to vendor warranty replacement" ||
          movementType === "oem warranty replacement";

        // Remaining defective = Defect Adjustment in − RTV Defective/Warranty Replacement out.
        let defectDelta = 0;
        if (isDefectAdjustment) {
          const raw =
            row.defectiveQuantity != null && row.defectiveQuantity !== ""
              ? Number(row.defectiveQuantity)
              : qty;
          if (Number.isFinite(raw) && raw !== 0) {
            defectDelta = Math.abs(raw);
          }
        } else if (isRtvDefectiveReplacement || isRtvWarrantyReplacement) {
          const raw =
            row.defectiveQuantity != null && row.defectiveQuantity !== ""
              ? Number(row.defectiveQuantity)
              : qty;
          if (Number.isFinite(raw) && raw !== 0) {
            defectDelta = -Math.abs(raw);
          }
        }

        if (qty === 0 && defectDelta === 0) continue;

        const existing = byBatch.get(batchNumber) || {
          batchNumber,
          availableQty: 0,
          defectiveQty: 0,
          purchasePricePerUnit: null,
        };
        existing.availableQty += qty;
        existing.defectiveQty += defectDelta;
        if (
          existing.purchasePricePerUnit == null &&
          row.purchasePricePerUnit != null &&
          row.purchasePricePerUnit !== ""
        ) {
          const price = Number(row.purchasePricePerUnit);
          if (Number.isFinite(price)) existing.purchasePricePerUnit = price;
        }
        byBatch.set(batchNumber, existing);
      }

      const totalPages = Number(pageResult.totalPages ?? 0);
      const pageNumber = Number(pageResult.pageNumber ?? pageResult.number ?? page);
      const last =
        pageResult.last != null
          ? Boolean(pageResult.last)
          : totalPages > 0
            ? pageNumber >= totalPages - 1
            : journalRows.length < TRANSFER_PAGE_SIZE;
      hasMore = !last && journalRows.length > 0;
      page += 1;
      if (page > 100) break;
    }

    return {
      content: Array.from(byBatch.values())
        .map((b) => ({
          ...b,
          defectiveQty: Math.max(0, Number(b.defectiveQty) || 0),
        }))
        .filter(
          (b) =>
            includeZeroAvailable ||
            b.availableQty > 0 ||
            b.defectiveQty > 0
        ),
    };
  },

  /** POST /saas/ims/stock-transfer — IMS05A */
  createStockTransfer: async (payload = {}) => {
    const body = {
      productId: toNumberOrNull(payload.productId),
      batchNumber: String(payload.batchNumber || "").trim(),
      transferQuantity: Number(payload.transferQuantity ?? payload.transferQty),
      purchasePricePerUnit: Number(
        payload.purchasePricePerUnit ?? payload.purchasePrice ?? 0
      ),
      transferDate: payload.transferDate,
      remarks: payload.remarks ? String(payload.remarks) : null,
      toName: String(payload.toName || "").trim(),
    };

    const sourceWarehouseId = toNumberOrNull(payload.sourceWarehouseId);
    if (sourceWarehouseId != null) {
      body.sourceWarehouseId = sourceWarehouseId;
    }

    const transferType = emptyToNull(payload.transferType);
    if (transferType) body.transferType = transferType;

    const destinationWarehouseId = toNumberOrNull(payload.destinationWarehouseId);
    if (destinationWarehouseId != null) {
      body.destinationWarehouseId = destinationWarehouseId;
    }

    const fromName = emptyToNull(payload.fromName);
    if (fromName) body.fromName = fromName;

    const warrantyType = emptyToNull(payload.warrantyType);
    if (warrantyType) body.warrantyType = warrantyType;

    body.invoiceNumber = emptyToNull(payload.invoiceNumber);

    const vendorId = toNumberOrNull(payload.vendorId);
    if (vendorId != null) body.vendorId = vendorId;

    const customerId = toNumberOrNull(payload.customerId);
    if (customerId != null) body.customerId = customerId;

    const challanTo = emptyToNull(payload.challanTo) || emptyToNull(payload.toName);
    if (challanTo) body.challanTo = challanTo;

    const challanToAddress = emptyToNull(payload.challanToAddress);
    if (challanToAddress) body.challanToAddress = challanToAddress;

    const challanToMobile = emptyToNull(payload.challanToMobile);
    if (challanToMobile) body.challanToMobile = challanToMobile;

    const response = await imsPost("/stock-transfer", body, STOCK_TRANSFER_ADD);
    return unwrapData(response) ?? { success: true };
  },

  /** GET /saas/ims/stock-position/{productId}/detail?customerId={customerId} — IMS05S */
  getStockPositionDetail: async (productId, allocationType, customerId) => {
    if (productId == null || String(productId).trim() === "") {
      throw new Error("Product ID is required");
    }
    const params = new URLSearchParams();
    const type = emptyToNull(allocationType);
    if (type) params.set("allocationType", String(type));
    const custId = toNumberOrNull(customerId);
    if (custId != null) params.set("customerId", String(custId));
    const query = params.toString();
    const response = await imsGet(
      `/stock-position/${encodeURIComponent(productId)}/detail${
        query ? `?${query}` : ""
      }`,
      STOCK_POSITION_SEARCH
    );
    const mapped = mapStockPositionDetail(unwrapItem(response));
    if (!mapped.product) {
      throw new Error("Product not found");
    }
    if (!mapped.product.productId) {
      mapped.product.productId = String(productId);
    }
    return mapped;
  },

  /** GET /saas/ims/stock-journal/reorder-alerts — IMS05R */
  getReorderAlerts: async () => {
    const response = await imsGet("/stock-journal/reorder-alerts", REORDER_ALERTS_SEARCH);
    const page = unwrapPage(response);
    const byProduct = new Map();

    for (const row of page.content || []) {
      if (!row || typeof row !== "object") continue;

      const productId = firstDefined(row.productId, row.product_id) ?? null;
      const partNumber = firstDefined(row.partNumber, row.part_number) || "—";
      const systemName = firstDefined(row.systemName, row.system_name) || "—";
      const mergeKey = [productId, partNumber, systemName].join("::");
      const alertType = String(
        firstDefined(row.alertType, row.alert_type) || ""
      ).toUpperCase();
      const reorderQuantity = toFiniteNumber(
        firstDefined(row.reorderQuantity, row.reorder_quantity, row.reorderQty)
      );
      const shortageQuantity = toFiniteNumber(
        firstDefined(row.shortageQuantity, row.shortage_quantity)
      );
      const totalAvailable = toFiniteNumber(
        firstDefined(row.totalAvailable, row.total_available)
      );

      const existing = byProduct.get(mergeKey) || {
        productId,
        productName:
          firstDefined(row.productName, row.product_name, row.name) || "—",
        systemName,
        partNumber,
        totalAvailable,
        salesSafetyStock: null,
        serviceSafetyStock: null,
        salesShortageQuantity: null,
        serviceShortageQuantity: null,
      };

      if (alertType === "SALES") {
        existing.salesSafetyStock = reorderQuantity;
        existing.salesShortageQuantity = shortageQuantity;
      } else if (alertType === "SERVICE") {
        existing.serviceSafetyStock = reorderQuantity;
        existing.serviceShortageQuantity = shortageQuantity;
      }

      existing.totalAvailable = totalAvailable;
      byProduct.set(mergeKey, existing);
    }

    return Array.from(byProduct.values());
  },
};
